//#region Imports
import React, { useState, useEffect, Fragment, useRef } from "react";
import sharedDatas from "../../businessLogic/sharedDatas";
import {
  TIPO_ANAGRAFICA_CLIENTE_PF_KEY,
  TIPO_ANAGRAFICA_CLIENTE_PG_KEY,
} from "../../businessLogic/constants/baseTableKeys";

//oneFront SDK
import {
  useSnackbar,
  useQuery,
  useMutation,
  useTranslation,
} from "@1f/react-sdk";

import VaporPage from "@vapor/react-custom/VaporPage";
import VaporToolbar from "@vapor/react-custom/VaporToolbar";
import VaporTag from "@vapor/react-custom/VaporTag";

//Vapor Components
import Grid from "@vapor/react-material/Grid";
import Stack from "@vapor/react-material/Stack";
import IconButton from "@vapor/react-material/IconButton";
import Checkbox from "@vapor/react-material/Checkbox";
import Divider from "@vapor/react-material/Divider";
import Button from "@vapor/react-material/Button";
import TextField from "@vapor/react-material/TextField";
import Autocomplete from "@vapor/react-material/Autocomplete";
import Switch from "@vapor/react-material/Switch";
import FormGroup from "@vapor/react-material/FormGroup";
import FormControlLabel from "@vapor/react-material/FormControlLabel";
import AccordionSummary from "@vapor/react-material/AccordionSummary";
import Tooltip from "@vapor/react-material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import AccordionDetails from "@vapor/react-material/AccordionDetails";
import TableContainer from "@vapor/react-material/TableContainer";
import Table from "@vapor/react-material/Table";
import TableBody from "@vapor/react-material/TableBody";
import TableRow from "@vapor/react-material/TableRow";
import TableCell from "@vapor/react-material/TableCell";
import Paper from "@vapor/react-material/Paper";
import Box from "@vapor/react-material/Box";

import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import ExtendedAccordion from "@vapor/react-extended/ExtendedAccordion";

//Vapor Date Pickers
import { AdapterDateFns } from "@vapor/react-x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@vapor/react-x-date-pickers/LocalizationProvider";
import { DatePicker } from "@vapor/react-x-date-pickers/DatePicker";
import { it } from "date-fns/locale";

//Icons
import {
  AddOutlined as AddIcon,
  ModeEdit as ModeEditIcon,
  Delete as DeleteIcon,
  SaveOutlined as SaveIcon,
  CloseOutlined as CancelIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
  ExpandMore as ExpandMoreIcon,
  AccountBalance as AccountBalanceIcon,
  Work as WorkIcon,
  Gavel as GavelIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";

//Custom components
import { Loading } from "../Loading";
import { NotAuth } from "../NotAuth";
import { NoData } from "../NoData";
import { ModalDelete } from "../ModalDelete";

//Constants, Api and Data Models
import { bsaPrestazione } from "../../businessLogic/constants/securityAreas";
import { isDateValid } from "../../businessLogic/helpers/dateHelpers";
import { getPrestPrefix } from "../../businessLogic/helpers/prestazioneHelpers";
import {
  getLocalStorageSelectedEntity,
  removeLocalStorageSelectedEntity,
} from "../../businessLogic/helpers/storageHelpers";

import {
  userPermissionModel,
  prestazioneModel,
  prestazionePagamentoModel,
  baseTipoPrestazioneModel,
  baseComuneModel,
  baseValutaModel,
} from "../../businessLogic/models";

import {
  query_users_byCompanyId,
} from "../../businessLogic/query/userQueries";

import {
  query_base_all,
  query_base_app_setting,
  query_base_tipo_prestazione,
  query_base_comune, 
  query_base_tipo_prestazione_ByDate,
} from "../../businessLogic/query/baseTablesQueries";

import {
  query_prestazioni_byFascicolo,
} from "../../businessLogic/query/prestazioniQueries";

import {
  query_anagrafiche_byFascicolo,
} from "../../businessLogic/query/anagraficaQueries";

import { mutation_add_audit } from "../../businessLogic/mutations/auditMutations";
import { mutation_add_prestazione, mutation_update_prestazione, mutation_delete_prestazione } from "../../businessLogic/mutations/prestazioneMutations";
import { mutation_add_prestazione_users,mutation_delete_prestazione_user } from "../../businessLogic/mutations/prestazioneUsersMutations";
import { mutation_add_prestazione_pagamento,  mutation_update_prestazione_pagamento, mutation_delete_prestazione_pagamento} from "../../businessLogic/mutations/mezziDiPagamentoMutations";


import {createAnagraficaModel, createPrestazioneModel, createUserModel} from "../../businessLogic/helpers/modelHelpers";
//Other components
import { v4 as uuidv4 } from "uuid";
import AutoNumeric from "autonumeric";
import { EuroConferenceApi } from "../../businessLogic/api/euroconferenceApi";
import { LogApi } from "../../businessLogic/api/logApi";
import { FascicoloApi } from "../../businessLogic/api/fascicoloApi";
import { CurrencyApi } from "../../businessLogic/api/currencyApi";
import BaseTableApi from "../../businessLogic/api/baseTableApi";
//#endregion

export const PrestazioneForm = ({
  fascicolo,
  onRischio,
  reloadStatusFascicolo,
}) => {
  const tenantId = sharedDatas.getPropertyByName("tenantId");
  const aziendaId = sharedDatas.getPropertyByName("aziendaId");
  const loggedUserId = sharedDatas.getPropertyByName("userId");
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const prestFromNavigation = getLocalStorageSelectedEntity();
  const euroConferenceApi = EuroConferenceApi();
  const logApi = LogApi();
  const fascicoloApi = FascicoloApi()
  const currencyApi = CurrencyApi();
  const baseTableApi = BaseTableApi();

  //#region Query
  const apiUsersByCompanyId = useQuery(
    query_users_byCompanyId,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );

  const apiBaseAll = useQuery(
    query_base_all,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );

  const apiBaseAppSetting = useQuery(
    query_base_app_setting,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiBaseTipoPrestazione = useQuery(
    query_base_tipo_prestazione,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiBaseTipoPrestazioneByDate = useQuery(
    query_base_tipo_prestazione_ByDate,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiBaseComune = useQuery(
    query_base_comune,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiPrestazioniByFascicolo = useQuery(
    query_prestazioni_byFascicolo,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiAnagraficaByFascicolo = useQuery(
    query_anagrafiche_byFascicolo,
    {},
    { lazy: true, graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  //#endregion

  //#region Mutation
  const apiAddAudit = useMutation(mutation_add_audit, {
    graphqlEndpoint: "aml:hasura:api://v1/graphql",
  });
  const apiAddPrestazione = useMutation(mutation_add_prestazione, {
    graphqlEndpoint: "aml:hasura:api://v1/graphql",
  });
  const apiUpdatePrestazione = useMutation(mutation_update_prestazione, {
    graphqlEndpoint: "aml:hasura:api://v1/graphql",
  });
  const apiDeletePrestazione = useMutation(mutation_delete_prestazione, {
    graphqlEndpoint: "aml:hasura:api://v1/graphql",
  });
  const apiAddPrestazioneUsers = useMutation(mutation_add_prestazione_users, {
    graphqlEndpoint: "aml:hasura:api://v1/graphql",
  });
  const apiDeletePrestazioneUser = useMutation(
    mutation_delete_prestazione_user,
    { graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiAddPrestazionePagamento = useMutation(
    mutation_add_prestazione_pagamento,
    { graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiUpdatePrestazionePagamento = useMutation(
    mutation_update_prestazione_pagamento,
    { graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  const apiDeletePrestazionePagamento = useMutation(
    mutation_delete_prestazione_pagamento,
    { graphqlEndpoint: "aml:hasura:api://v1/graphql" }
  );
  //#endregion

  //#region UseStates
  const [backDropOpen, setBackDropOpen] = useState(true); //Loader utilizzato durante il caricamento
  const [isDataLoaded, setIsDataLoaded] = useState(false); //Indica lo stato di caricamento del result Api
  const [userPermissions, setUserPermissions] = useState(
    new userPermissionModel(
      0,
      aziendaId,
      bsaPrestazione,
      false,
      false,
      false,
      false
    )
  );

  //Anagrafica cliente
  const [anagraficaFascicolo, setAnagraficaFascicolo] = useState();
  const [anagIdentDate, setAnagIdentDate] = useState(fascicolo?.date ?? null);
  //Prestazione
  const [showPrestazioneForm, setShowPrestazioneForm] = useState(false);
  const [currentPrestazioniList, setCurrentPrestazioniList] = useState([]); //Contiene le prestazioni correnti
  const [allPrestazioniList, setAllPrestazioniList] = useState([]); //Contiene tutte le prestazioni (tutti i tipi tenant)
  const [showAllPrestazioni, setShowAllPrestazioni] = useState(false); //Toggle vista: false = solo le mie, true = tutte
  const [filterTipoTenantId, setFilterTipoTenantId] = useState(0); //Filtro per tipo tenant: 0 = tutti, 1/2/3 = specifico
  const [isPrestazioneReadOnly, setIsPrestazioneReadOnly] = useState(false); //Read-only per prestazioni di altri tipi
  const [currentPrestazione, setCurrentPrestazione] = useState(
    new prestazioneModel(0, "N")
  ); //Contiene la prestazione corrente
  const [currentPrestazioneOriginal, setCurrentPrestazioneOriginal] = useState(
    new prestazioneModel(0, "N")
  ); //Used for Audit
  const [currentPrestazioneTipoKeyS, setCurrentPrestazioneTipoKeyS] =
    useState("Normale"); //N: Normale, M: Multipla, F: Frazionata
  const [isPaeseIta, setIsPaeseIta] = useState(true);
  const [currentPrestazioneChbVal, setCurrentPrestazioneChbVal] = useState({
    isPrivate: false,
    isEsoneroObbAst: false,
  });

  //Mezzi di pagamento
  const [currentPrestazionePagamentiList, setCurrentPrestazionePagamentiList] =
    useState([]);
  const [currentPrestazionePagamento, setCurrentPrestazionePagamento] =
    useState(
      new prestazionePagamentoModel(
        0,
        0,
        null,
        0,
        0,
        0,
        undefined,
        undefined,
        null,
        null,
        "",
        "",
        "",
        new Date(),
        loggedUserId,
        "EUR"
      )
    ); //Contiene i pagamenti relativi alla prestazione corrente
  const [
    currentPrestazionePagamentoOriginal,
    setCurrentPrestazionePagamentoOriginal,
  ] = useState(
    new prestazionePagamentoModel(
      0,
      0,
      null,
      0,
      0,
      0,
      undefined,
      undefined,
      null,
      null,
      "",
      "",
      "",
      new Date(),
      loggedUserId,
      "EUR"
    )
  ); //Used for Audit
  const [pagamentoImportoEuro, setPagamentoImportoEuro] = useState(0);
  const [sogliaPagamentoContanti, setSogliaPagamentoContanti] = useState(0);

  //Professionisti
  const [utentiList, setUtentiList] = useState([]);

  //Tabelle di base
  const [baseTipoPrestazioneList, setBaseTipoPrestazioneList] = useState([]);
  const [baseScopoPrestazioneList, setBaseScopoPrestazioneList] = useState([]);
  const [baseNaturaPrestazioneList, setBaseNaturaPrestazioneList] = useState(
    []
  );
  const [baseOrigineFondiList, setBaseOrigineFondiList] = useState([]);
  const [basePaeseList, setBasePaeseList] = useState([]);
  const [baseProvinciaList, setBaseProvinciaList] = useState([]);
  const [baseComuneList, setBaseComuneList] = useState([]);
  const [baseValutaList, setBaseValutaList] = useState([]);
  const [,setValutaByIso] = useState([])
  const [baseMezzoPagamentoList, setBaseMezzoPagamentoList] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false); //Modal per la conferma di eliminazione di un record
  const [idToPerform, setIdToPerform] = useState(0); //Contiene l'id del record da eliminare
  const [typeToPerform, setTypeToPerform] = useState("P"); //Contiene il tipo di record da eliminare. P: Prestazione, MP: Mezzo di pagamento

  //Controlli form
  const [canIshowMezziPag, setCanIShowMezziPag] = useState(
    currentPrestazionePagamento.id !== 0
  );
  const [showMezziPag, setShowMezziPag] = useState(false);
  const [isValutaEstera, setIsValutaEstera] = useState(false);
  const [, setValutaCambio] = useState(0);
  const [valutaCambioInput, setValutaCambioInput] = useState("");
  //#endregion
  
  var tenantTypeId = sharedDatas.getPropertyByName("userModel").tenantTipoId;
  const otherPrestazioniCount = allPrestazioniList.filter(
    (prestazione) => prestazione.tipoTenantId !== tenantTypeId
  ).length;

  //Autonumeric
  const txtImportoRef = useRef();
  // eslint-disable-next-line no-unused-vars
  let txtImportoAutoNum = null;
  useEffect(() => {
    if (txtImportoRef.current !== undefined && txtImportoRef.current !== null) {
      const txtImportoEl = txtImportoRef.current.children[1].children[0];
      // eslint-disable-next-line react-hooks/exhaustive-deps
      txtImportoAutoNum = new AutoNumeric(txtImportoEl, {
        decimalCharacter: ",",
        digitGroupSeparator: ".",
        decimalPlaces: 2,
      });
    }
  });

  useEffect(() => {
    loadDatas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataLoaded]);

  //#region Load methods
  async function loadDatas() {
    try {
      if (!isDataLoaded) {
        setBackDropOpen(true);

        /**************************************/
        //Carico i permessi relativi all'utente
        setUserPermissions(
          sharedDatas
            .getPropertyByName("userPermissions")
            .filter((x) => x.area === bsaPrestazione)[0]
        );        
        /**************************************/
        //Carico le tabelle di base
        //base_app_setting
        const appSettResponse = await apiBaseAppSetting.fetch({
          key: "soglia_pagamento_contanti",
        });
        if (appSettResponse.data.data.base_app_setting.length > 0) {
          setSogliaPagamentoContanti(
            parseInt(appSettResponse.data.data.base_app_setting[0].Value)
          );
        }

        const baseTables = await loadAllBaseTables(); 
        await loadBaseTipoPrestazione(tenantTypeId,currentPrestazione?.dataStart ?? new Date());

        //base_valuta
        let currencyResponse = await currencyApi.getCurrencies()
        const valutaItems = [];
        currencyResponse.map((item) =>
          valutaItems.push(
            new baseValutaModel(
              item.isoCode,
              item.currency,
              item.country
            )
          )
        )
        setBaseValutaList(valutaItems);

        /**************************************/
        //Carico gli utenti
        const usersResponse = await apiUsersByCompanyId.fetch({
          tenantId: tenantId,
          aziendaId: aziendaId,
        });
        const arrUsers = [];
        usersResponse.data.data.user_tenant.map((item) =>
          arrUsers.push(
            createUserModel(item)            
          )
        );
        setUtentiList(arrUsers);

        //gli utenti originali li confronto con i professionisti indicati nel fascicolo
        //così da presentare l'elenco di questi ultimi
        const newArrUsers = arrUsers;

        //prestazioni
        if (fascicolo.id > 0) {
          const prestFascResponse = await apiPrestazioniByFascicolo.fetch({
            fascicoloId: fascicolo.id,
          });
          if (prestFascResponse.data.errors !== undefined) {
            enqueueSnackbar(t("error.generale"), { variant: "error" });
          } else {
            const arrItems = [];
            const arrAllItems = [];
            prestFascResponse.data.data.prestazione.forEach((item) => {
              let itemModel = createPrestazioneModel(item)              
  
              const arrItemsUsers = [];
              item.prestazione_users.forEach((itemU) => {
                newArrUsers.forEach((item3) => {
                  if (itemU.UserId === item3.userId) {
                    arrItemsUsers.push(item3);
                  }
                });
              });
  
              const arrItemsPagamenti = [];
              item.prestazione_pagamentos.forEach((itemP) => {
                arrItemsPagamenti.push(
                  new prestazionePagamentoModel(
                    itemP.Id,
                    itemP.ValutaId,
                    itemP.ValutaData,
                    itemP.ValutaCambio,
                    itemP.ValutaImporto,
                    itemP.ImportoEuro,
                    itemP.ModalitaPagamentoId,
                    itemP.OrigineId,
                    itemP.CreatedOn,
                    itemP.ModifiedOn,
                    "",
                    itemP.base_mezzo_pagamento.Value,
                    itemP.base_origine_fondi.Value,
                    itemP.Data,
                    itemP.CreatedBy,
                    itemP.ModifiedBy,
                    itemP.IsoCode
                  )
                );
              });
  
              itemModel.professionisti = arrItemsUsers;
              itemModel.pagamenti = arrItemsPagamenti;
              arrAllItems.push(itemModel);
              if(item.base_tipo_prestazione.BaseTenantTipoId === tenantTypeId){
                arrItems.push(itemModel);
              }
            });

            setCurrentPrestazioniList(arrItems);
            setAllPrestazioniList(arrAllItems);

            const scopoItemId = baseTables.scopoPrestazione.filter(
              (x) => x.key === "base_scopo_prestazione_1"
            )[0].id;
            const naturaItemId = baseTables.naturaPrestazione.filter(
              (x) => x.key === "base_natura_prestazione_3"
            )[0].id;
            let paeseId = baseTables.paese.filter(
              (x) => x.identificativo === "ITA"
            )[0].id;
            let provinciaId = undefined;
            let provincia = "";
            let cittaId = undefined;
            let citta = "";

            //Recupero l'anagrafica cliente del fascicolo per ottenere paese, provincia e città di default
            const anagResponse = await apiAnagraficaByFascicolo.fetch({
              fascicoloId: fascicolo.id,
            });
            if (anagResponse.data.errors !== undefined) {
            } else {
              for (
                var i = 0;
                i < anagResponse.data.data.anagrafica.length;
                i++
              ) {
                const item = anagResponse.data.data.anagrafica[i];
                let itemModel = createAnagraficaModel(item)                

                for (let n = 0; n < item.anagrafica_fascicolos.length; n++) {
                  const anagFasc = item.anagrafica_fascicolos[n];
                  const tipoAnagKey = baseTables.tipoAnagrafica.filter(
                    (bta) => bta.id === anagFasc.base_tipo_anagrafica.Id
                  )[0].key;
                  if (
                    tipoAnagKey === TIPO_ANAGRAFICA_CLIENTE_PF_KEY ||
                    tipoAnagKey === TIPO_ANAGRAFICA_CLIENTE_PG_KEY
                  ) {
                    setAnagraficaFascicolo(itemModel);

                    paeseId = itemModel.addrPaeseId;
                    provinciaId = itemModel.addrProvinciaId;
                    provincia = itemModel.addrProvincia;
                    cittaId = itemModel.addrComuneId;
                    citta = itemModel.addrComune;

                    if (paeseId !== null && paeseId > 0) {
                      const paese = baseTables.paese.filter(
                        // eslint-disable-next-line no-loop-func
                        (item) => item.id === paeseId
                      )[0];
                      if (paese.identificativo === "ITA") {
                        if (
                          provinciaId !== null &&
                          provinciaId !== undefined &&
                          provinciaId > 0
                        ) {
                          const provincia = baseTables.prov.filter(
                            // eslint-disable-next-line no-loop-func
                            (item) => item.id === provinciaId
                          )[0];
                          loadBaseComune(provincia.sigla);
                        }
                      }

                      setIsPaeseIta(paese.identificativo === "ITA");
                    }
                  }
                }
              }
            }

            const prestFromNavigationResult = prestFromNavigation;
            let prestFromNavigationResultJson = null;
            // getLocalStorageSelectedEntity() restituisce gia' un oggetto: fare
            // JSON.parse su un oggetto genererebbe un SyntaxError. Gestiamo
            // entrambi i casi (stringa o oggetto) come in AnagraficaForm.
            if (
              prestFromNavigationResult !== null &&
              prestFromNavigationResult !== undefined
            ) {
              prestFromNavigationResultJson =
                typeof prestFromNavigationResult === "string"
                  ? JSON.parse(prestFromNavigationResult)
                  : prestFromNavigationResult;
            }

            let itemToSelect = null;
            if (
              prestFromNavigationResultJson !== null &&
              prestFromNavigationResultJson.entityType === "prestazione"
            ) {
              const targetId = prestFromNavigationResultJson.entityId;
              // La prestazione scelta puo' appartenere a un altro tipo tenant:
              // in tal caso non e' in currentPrestazioniList (arrItems) ma solo
              // in allPrestazioniList (arrAllItems). Cerchiamo in entrambe.
              itemToSelect =
                arrItems.find((x) => x.id === targetId) ||
                arrItems.find((x) => String(x.id) === String(targetId)) ||
                arrAllItems.find((x) => x.id === targetId) ||
                arrAllItems.find((x) => String(x.id) === String(targetId)) ||
                null;
              // Se e' di un altro tipo tenant, attiviamo la vista "tutte" cosi'
              // la riga selezionata risulta visibile in tabella e forziamo la
              // sola lettura (come handleViewPrestClick), altrimenti verrebbero
              // mostrati i mezzi di pagamento e i campi editabili di una
              // prestazione appartenente a un tipo tenant diverso.
              if (itemToSelect && itemToSelect.tipoTenantId !== tenantTypeId) {
                setShowAllPrestazioni(true);
                setIsPrestazioneReadOnly(true);
              }
              removeLocalStorageSelectedEntity();
            }

            if (itemToSelect) {
              await selectPrestazione(
                itemToSelect,
                baseTables.paese,
                baseTables.prov,
                valutaItems
              );
            } else {
              const professionistiArr = [];
              professionistiArr.push(
                sharedDatas.getPropertyByName("userModel")
              );
              let anagCliente = null;
              anagResponse.data.data.anagrafica.forEach((anag) => {
                if (
                  anag.anagrafica_fascicolos.filter(
                    (af) => af.TipoAnagId === 1 || af.TipoAnagId === 2
                  ).length > 0
                )
                  anagCliente = anag;
              });
              const newItem = new prestazioneModel(
                0,
                "N",
                undefined,
                "",
                "",
                "",
                anagCliente? new Date(
                  anagCliente.IdentifiedOn
                ) : new Date(),
                null,
                paeseId,
                provinciaId,
                provincia,
                cittaId,
                citta,
                scopoItemId,
                naturaItemId,
                "",
                false,
                undefined,
                undefined,
                professionistiArr,
                [],
                "",
                false,
                "",
                "",
                [],
                0,
                loggedUserId,
                "",
                "",
                "",
                ""
              );
              setCurrentPrestazione(newItem);
              setCurrentPrestazioneOriginal(newItem);
              setAnagIdentDate((anagCliente && anagCliente.IdentifiedOn) || fascicolo.date);
            }
          }
        }
      }
    } catch (e) {
      logApi.AddLog(e.message,e.stack,"LoadDatas","PrestazioneForm")      
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    } finally {
      setBackDropOpen(false);
      setIsDataLoaded(true);
    }
  }
  async function loadAllBaseTables() {
    /**************************************/
    //Carico le tabelle di base
    let tbAllBaseResponse = null 
    try {
      tbAllBaseResponse = await apiBaseAll.fetch({tenantId: tenantId});
      if(!tbAllBaseResponse || tbAllBaseResponse.error){
        throw new Error("Errore interno API: " + tbAllBaseResponse.error);
      }
    } catch (error) {
      const errorMessage = error?.message || JSON.stringify(error);
      throw new Error("Impossibile recuperare le tabelle di base. " + errorMessage);
    }

    try{
      /*base_scopo_prestazione*/
      const arrScopoPrestItems = baseTableApi.GetBaseScopoPrestazione(tbAllBaseResponse);
      setBaseScopoPrestazioneList(arrScopoPrestItems);
      /*base_natura_prestazione*/
      const arrNaturaPrestItems = baseTableApi.GetBaseNaturaPrestazione(tbAllBaseResponse);
      setBaseNaturaPrestazioneList(arrNaturaPrestItems);
      /*base_origine_fondi*/
      const arrOrigineFondiItems = baseTableApi.GetBaseOrigineFondi(tbAllBaseResponse);
      setBaseOrigineFondiList(arrOrigineFondiItems);
      /*base_mezzo_pagamento*/
      const arrBaseMezzoPagItems = baseTableApi.GetBaseMezzoPagamento(tbAllBaseResponse);
      setBaseMezzoPagamentoList(arrBaseMezzoPagItems);
      /*base_paese*/
      const arrPaeseItems = baseTableApi.GetBasePaese(tbAllBaseResponse);
      setBasePaeseList(arrPaeseItems);
      /*base_provincia*/
      const arrProvinciaItems = baseTableApi.GetBaseProvincia(tbAllBaseResponse);
      setBaseProvinciaList(arrProvinciaItems);
      /*base_tipo_anagrafica*/
      const arrTipoAnagItems = baseTableApi.GetBaseTipoAnagrafica(tbAllBaseResponse);
      return {
        scopoPrestazione : arrScopoPrestItems,
        naturaPrestazione : arrNaturaPrestItems,
        origineFondi : arrOrigineFondiItems,
        mezzoPagamento : arrBaseMezzoPagItems,
        paese : arrPaeseItems,
        prov : arrProvinciaItems,
        tipoAnagrafica : arrTipoAnagItems
      }
    } catch (e) {
      logApi.AddLog(e.message,e.stack,"LoadAllBaseTables","PrestazioneForm")
    }
  }
  const loadBaseTipoPrestazione  = async (tenantTypeId, dataStart) => {
    if(dataStart && isDateValid(dataStart)){
      try {
        let prefixPrest = getPrestPrefix(tenantTypeId)
        const tipoPrestByDateResp = await apiBaseTipoPrestazioneByDate.fetch({
          tenantType : tenantTypeId,
          dateStart : new Date(dataStart).toISOString().split('T')[0]
        })
        const tipoPrestItems = [];
        tipoPrestByDateResp.data.data.base_tipo_prestazione.map((item) =>
          tipoPrestItems.push(
            new baseTipoPrestazioneModel(
              item.Id,
              prefixPrest + " - " + item.Nome,
              item.Rischio,
              item.Regole,
              item.DataInizioValidita,
              item.DataFineValidita
            )
          )
        );
        setBaseTipoPrestazioneList(tipoPrestItems);
      } catch (e) {
        logApi.AddLog(e.message + " TenantType: " + tenantTypeId,e.stack,"LoadBaseTipoPrestazioneOnDateChange","PrestazioneForm")      
        enqueueSnackbar(t("error.generale"), { variant: "error" });
      }
    }    
  }
  const loadBaseComune = async (provincia) => {
    try {
      const comuneResponse = await apiBaseComune.fetch({
        provincia: provincia,
      });
      const arrItems = [];
      comuneResponse.data.data.base_comune.map((item) =>
        arrItems.push(
          new baseComuneModel(item.Id, item.Codice, item.Provincia, item.Nome)
        )
      );
      setBaseComuneList(arrItems);
    } catch (e) {
      logApi.AddLog(e.message + " Provincia: " + provincia,e.stack,"LoadBaseComune","PrestazioneForm")      
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    }
  };
  //#endregion

  //Salvataggio degli inputs Prestazione
  const evaluateTipoPrestazione = (professionisti, pagamenti) => {
    let itemToReturn = "N";

    if (professionisti !== undefined) {
      if (professionisti.length === 1) {
        setCurrentPrestazioneTipoKeyS("Normale");
        itemToReturn = "N";
      } else if (professionisti.length > 1) {
        setCurrentPrestazioneTipoKeyS("Multipla");
        itemToReturn = "M";
      }
    } else {
      setCurrentPrestazioneTipoKeyS("Normale");
    }

    if (pagamenti !== undefined) {
      let totale = 0;

      pagamenti.forEach((item) => {
        totale = totale + item.importo;
      });
      if (pagamenti.length > 1) {
        setCurrentPrestazioneTipoKeyS("Frazionata");
        itemToReturn = "F";
      }
    }

    return itemToReturn;
  };

  //#region Handler
  const handleChange = (event, value) => {
    const { id } = event.target;

    if (id.indexOf("ddlPrestazione-option") !== -1 || id === "ddlModIdent") {
      currentPrestazione.prestazioneId = value.id;
    } else if (
      id.indexOf("ddlUtenti-option") !== -1 ||
      id === "ddlUtenti" ||
      id === "chbDdlUtenti"
    ) {
      currentPrestazione.professionisti = value;
    } else if (id.indexOf("ddlScopo-option") !== -1 || id === "ddlScopo") {
      currentPrestazione.scopoId = value.id;
    } else if (id.indexOf("ddlNatura-option") !== -1 || id === "ddlNatura") {
      currentPrestazione.naturaId = value.id;
    } else if (id.indexOf("ddlPaese-option") !== -1 || id === "ddlPaese") {
      currentPrestazione.paeseId = value.id;

      //Recupero le province
      var country = basePaeseList.filter(
        (item) => item.id === currentPrestazione.paeseId
      )[0];
      if (country.identificativo === "ITA") {
        currentPrestazione.provincia = "";
        currentPrestazione.citta = "";
      } else {
        currentPrestazione.provinciaId = null;
        currentPrestazione.cittaId = null;
      }

      setIsPaeseIta(country.identificativo === "ITA");

      //Visualizzo il rischio
      if (country.isRischio) {
        enqueueSnackbar(t("anagrafica.message.paeseRischio"), {
          variant: "error",
        });
      }
    } else if (
      id.indexOf("ddlProvincia-option") !== -1 ||
      id === "ddlProvincia"
    ) {
      currentPrestazione.provinciaId = value.id;

      //Recupero le città
      var provincia = baseProvinciaList.filter(
        (item) => item.id === currentPrestazione.provinciaId
      )[0];
      loadBaseComune(provincia.sigla);

      //Visualizzo il rischio
      switch (provincia.rischio) {
        case 1: {
          enqueueSnackbar(t("anagrafica.message.provinciaRischio1"), {
            variant: "success",
          });
          break;
        }
        case 2: {
          enqueueSnackbar(t("anagrafica.message.provinciaRischio2"), {
            variant: "warning",
          });
          break;
        }
        case 3: {
          enqueueSnackbar(t("anagrafica.message.provinciaRischio3"), {
            variant: "warning",
          });
          break;
        }
        case 4: {
          enqueueSnackbar(t("anagrafica.message.provinciaRischio4"), {
            variant: "error",
          });
          break;
        }
        default: {
          break;
        }
      }
    } else if (id === "txtProvincia") {
      currentPrestazione.provinciaId = null;
      currentPrestazione.provincia = event.target.value;
    } else if (id.indexOf("ddlComune-option") !== -1 || id === "ddlComune") {
      currentPrestazione.cittaId = value.id;
    } else if (id === "txtComune") {
      currentPrestazione.cittaId = null;
      currentPrestazione.citta = event.target.value;
    } else if (id === "txtDescrizione") {
      currentPrestazione.descrizione = event.target.value;
    } else if (id === "chkPrivate") {
      currentPrestazione.isPrivate = event.target.checked;
      setCurrentPrestazioneChbVal({ isPrivate: event.target.checked });
    } else if (id === "chkAstIsEsonero") {
      currentPrestazione.isEsoneroObbAst = event.target.checked;
      if (event.target.checked === false) {
        currentPrestazione.astensioneMotivo = "";
      }
      setCurrentPrestazioneChbVal({ isEsoneroObbAst: event.target.checked });
    } else if (id === "txtAstMotivo") {
      currentPrestazione.astensioneMotivo = event.target.value;
    } else if (id === "txtSettoreAttivita"){
      currentPrestazione.settoreAttivita = event.target.value;
    } else if (id === "txtSettorePrestazione"){
      currentPrestazione.settorePrestazione = event.target.value;
    } else if (id === "txtRagione"){
      currentPrestazione.ragione = event.target.value;
    }

    currentPrestazione.tipoKey = evaluateTipoPrestazione(
      currentPrestazione.professionisti,
      currentPrestazione.pagamenti
    );

    setCurrentPrestazione(currentPrestazione);
  };
  const handleStartDate = (date) => {
    if (date !== null && isDateValid(date)) {
      const newDate = new Date(date);
      currentPrestazione.dataStart = newDate;
      setCurrentPrestazione((prevState) => ({
        ...prevState,
        dataStart: newDate,
      }));

      if (currentPrestazionePagamento.id === 0) {
        setCurrentPrestazionePagamento((prevState) => ({
          ...prevState,
          data: newDate,
        }));
      }

      const now = new Date();
      const msBetweenDates = Math.abs(date.getTime() - now.getTime());
      const daysBetweenDates = msBetweenDates / (24 * 60 * 60 * 1000);
      if (daysBetweenDates >= 30) {
        enqueueSnackbar(t("prestazione.error.dateLessThan30Days"), {
          variant: "warning",
        });
      }
    }

    loadBaseTipoPrestazione(tenantTypeId,date)
  };
  const handleEndDate = (date) => {
    if (date !== null && isDateValid(date)) {
      if (
        date.setHours(0, 0, 0, 0) >=
        new Date(currentPrestazione.dataStart).setHours(0, 0, 0, 0)
      ) {
        currentPrestazione.dataEnd = date;
        setCurrentPrestazione((prevState) => ({ ...prevState, dataEnd: date }));
      } else {
        enqueueSnackbar(
          "La data della cessazione deve essere superiore alla data di inizio",
          { variant: "error" }
        );
      }
    } else {
      currentPrestazione.dataEnd = null;
      setCurrentPrestazione((prevState) => ({ ...prevState, dataEnd: date }));
    }
  };  
  //#endregion

  //CRUD della prestazione
  const emptyPrestazioneModel = () => {
    const scopoItemId = baseScopoPrestazioneList.filter(
      (x) => x.key === "base_scopo_prestazione_1"
    )[0].id;
    const naturaItemId = baseNaturaPrestazioneList.filter(
      (x) => x.key === "base_natura_prestazione_3"
    )[0].id;
    let paeseId = basePaeseList.filter((x) => x.identificativo === "ITA")[0].id;

    let provinciaId = undefined;
    let provincia = "";
    let cittaId = undefined;
    let citta = "";

    try {
      paeseId = anagraficaFascicolo?.addrPaeseId ?? paeseId;
      provinciaId = anagraficaFascicolo.addrProvinciaId;
      provincia = anagraficaFascicolo.addrProvincia;
      cittaId = anagraficaFascicolo.addrComuneId;
      citta = anagraficaFascicolo.addrComune;

      if (paeseId !== null) {
        const paese = basePaeseList.filter((item) => item.id === paeseId)[0];
        if (paese.identificativo === "ITA") {
          if (
            provinciaId !== null &&
            provinciaId !== undefined &&
            provinciaId > 0
          ) {
            const provincia = baseProvinciaList.filter(
              (item) => item.id === provinciaId
            )[0];
            loadBaseComune(provincia.sigla);
          }
        }

        setIsPaeseIta(paese.identificativo === "ITA");
      }
    } catch (e) {
      logApi.AddLog(e.message,e.stack,"EmptyPrestazioneModel","PrestazioneForm")      
    }

    const professionistiArr = [];
    professionistiArr.push(sharedDatas.getPropertyByName("userModel"));

    const newItem = new prestazioneModel(
      0,
      "N",
      null,
      "",
      "",
      "",
      new Date(anagIdentDate),
      null,
      paeseId ?? null,
      provinciaId ?? null,
      provincia ?? "",
      cittaId ?? null,
      citta ?? "",
      scopoItemId,
      naturaItemId,
      "",
      false,
      undefined,
      undefined,
      professionistiArr,
      [],
      "",
      false,
      "",
      "",
      [],
      0,
      loggedUserId,
      "",
      "",
      "",
      ""
    );
    return newItem;
  };

  //#region Handlers
  const handleAddPrestClick = async () => {
    setCurrentPrestazione(emptyPrestazioneModel());
    setCurrentPrestazioneOriginal(emptyPrestazioneModel());
    setCurrentPrestazioneTipoKeyS("Normale");
    setCurrentPrestazioneChbVal({ isPrivate: false, isEsoneroObbAst: false });
    setCurrentPrestazionePagamento(emptyPagamentoModel(null, baseValutaList));
    setCurrentPrestazionePagamentoOriginal(
      emptyPagamentoModel(null, baseValutaList)
    );
    setCurrentPrestazionePagamentiList([]);
    setShowMezziPag(false);
    setCanIShowMezziPag(false);
    setShowPrestazioneForm(true);

    var tenantTypeId = sharedDatas.getPropertyByName("userModel").tenantTipoId;
    await loadBaseTipoPrestazione(tenantTypeId,currentPrestazione?.dataStart ?? new Date());
  };
  const handleCancelClick = async () => {
    setCurrentPrestazione(emptyPrestazioneModel());
    setCurrentPrestazioneOriginal(emptyPrestazioneModel());
    setCurrentPrestazioneTipoKeyS("Normale");
    setCurrentPrestazioneChbVal({ isPrivate: false, isEsoneroObbAst: false });
    setCurrentPrestazionePagamento(emptyPagamentoModel(null, baseValutaList));
    setCurrentPrestazionePagamentoOriginal(
      emptyPagamentoModel(null, baseValutaList)
    );
    setCurrentPrestazionePagamentiList([]);
    setShowMezziPag(false);
    setCanIShowMezziPag(false);
    setShowPrestazioneForm(false);
    setIsPrestazioneReadOnly(false);

    var tenantTypeId = sharedDatas.getPropertyByName("userModel").tenantTipoId;
    await loadBaseTipoPrestazione(tenantTypeId,currentPrestazione?.dataStart ?? new Date());
  };
  const handleSelectedPrestClick = async (prestazione) => {
    try {
      setIsPrestazioneReadOnly(false);
      await selectPrestazione(
        prestazione,
        basePaeseList,
        baseProvinciaList,
        baseValutaList
      );
    } catch (e) {
      logApi.AddLog(e.message + " SelectedPrestazione: " + prestazione,e.stack,"HandleSelectPrestClick","PrestazioneForm")
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    } finally {
      setBackDropOpen(false);
    }
  };
  const handleViewPrestClick = async (prestazione) => {
    try {
      setIsPrestazioneReadOnly(true);
      await selectPrestazione(
        prestazione,
        basePaeseList,
        baseProvinciaList,
        baseValutaList
      );
    } catch (e) {
      logApi.AddLog(e.message + " ViewPrestazione: " + prestazione,e.stack,"HandleViewPrestClick","PrestazioneForm")
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    } finally {
      setBackDropOpen(false);
    }
  };
  const selectPrestazione = async (prestazione, paesi, province, valute) => {
    try {
      setBackDropOpen(true);
      setCurrentPrestazione(prestazione);
      setCurrentPrestazioneOriginal(prestazione);

      setCurrentPrestazionePagamento(emptyPagamentoModel(prestazione, valute));

      if (prestazione.paeseId !== null) {
        const paese = paesi.filter(
          (item) => item.id === prestazione.paeseId
        )[0];
        if (paese.identificativo === "ITA") {
          if (
            prestazione.provinciaId !== null &&
            prestazione.provinciaId !== undefined &&
            prestazione.provinciaId > 0
          ) {
            const provincia = province.filter(
              (item) => item.id === prestazione.provinciaId
            )[0];
            loadBaseComune(provincia.sigla);
          }
        }

        setIsPaeseIta(paese.identificativo === "ITA");
      }

      evaluateTipoPrestazione(
        prestazione.professionisti,
        prestazione.pagamenti
      );
      setCurrentPrestazionePagamentiList(prestazione.pagamenti);
      setCurrentPrestazioneChbVal({
        isPrivate: prestazione.isPrivate,
        isEsoneroObbAst: prestazione.isEsoneroObbAst,
      });
      setCanIShowMezziPag(true);
      setShowMezziPag(prestazione.pagamenti.length > 0);
      setShowPrestazioneForm(true);
      await loadBaseTipoPrestazione(prestazione.tipoTenantId,prestazione.dataStart ?? new Date());
    } catch (e) {
      logApi.AddLog(e.message + " Prestazione: "+ prestazione, e.stack,"SelectPrestazione","PrestazioneForm")
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    } finally {
      setBackDropOpen(false);
    }
  };

  const handleRischioProf = (prestazione, professionista) => {
    // Trova il rischio esistente per questo professionista
    const rischioEsistente = prestazione.rischi.find(r => r.ProvidedBy === professionista.userId);
    
    // Crea un oggetto prestazione con il professionista specifico pre-selezionato
    const prestazioneWithProf = {
      ...prestazione,
      selectedProfessionista: professionista,
      rischioEsistente: rischioEsistente || null
    };
    onRischio(prestazioneWithProf);
  };

  const handleDeletePrestClick = (prestazione) => {
    setIdToPerform(prestazione.id);
    setTypeToPerform("P");
    setShowDeleteModal(true);
  };
  const handleSaveClick = async () => {
    try {
      if (
        currentPrestazione.prestazioneId !== undefined &&
        currentPrestazione.prestazioneId !== null &&
        currentPrestazione.professionisti !== undefined &&
        currentPrestazione.professionisti.length > 0 &&
        currentPrestazione.dataStart !== undefined &&
        currentPrestazione.dataStart !== null &&
        currentPrestazione.scopoId !== undefined &&
        currentPrestazione.scopoId !== null &&
        currentPrestazione.naturaId !== undefined &&
        currentPrestazione.naturaId !== null &&
        currentPrestazione.paeseId !== undefined &&
        currentPrestazione.paeseId !== null         
      ) {
        setBackDropOpen(true);

        //Se sto creando la prestazione
        if (currentPrestazione.id === 0) {
          const newDateStartD = new Date(currentPrestazione.dataStart);
          const newDateStart = new Date(
            Date.UTC(
              newDateStartD.getFullYear(),
              newDateStartD.getMonth(),
              newDateStartD.getDate(),
              newDateStartD.getHours(),
              newDateStartD.getMinutes(),
              newDateStartD.getSeconds()
            )
          );

          let newDateEnd = null;
          if (currentPrestazione.dataEnd !== null) {
            const newDateEndD = new Date(currentPrestazione.dataEnd);
            newDateEnd = new Date(
              Date.UTC(
                newDateEndD.getFullYear(),
                newDateEndD.getMonth(),
                newDateEndD.getDate(),
                newDateEndD.getHours(),
                newDateEndD.getMinutes(),
                newDateEndD.getSeconds()
              )
            );
          }

          const addPresResponse = await apiAddPrestazione.fetch({
            fascicoloId: fascicolo.id,
            tipoKey: currentPrestazione.tipoKey,
            prestazioneId: currentPrestazione.prestazioneId,
            naturaId: currentPrestazione.naturaId,
            scopoId: currentPrestazione.scopoId,
            startDate: newDateStart,
            endDate: newDateEnd,
            paeseId: currentPrestazione.paeseId,
            provinciaId: currentPrestazione.provinciaId,
            provincia: currentPrestazione.provincia,
            cittaId: currentPrestazione.cittaId,
            citta: currentPrestazione.citta,
            descrizione: currentPrestazione.descrizione !== "" ? currentPrestazione.descrizione : "Non indicata",
            isPrivate: currentPrestazione.isPrivate,
            astensioneMotivo: currentPrestazione.astensioneMotivo,
            isEsoneroObbAst: currentPrestazione.isEsoneroObbAst,
            settoreAttivita: currentPrestazione.settoreAttivita,
            settorePrestazione: currentPrestazione.settorePrestazione,
            ragione: currentPrestazione.ragione, 
          });
          if (addPresResponse.data.errors !== undefined) {
            enqueueSnackbar(t("error.generale"), { variant: "error" });
          } else {
            const result = addPresResponse.data.data.insert_prestazione_one;
            let prestazioneResultModel = createPrestazioneModel(result)
            //Aggiungo la Audit per tracciare l'azione
            const newValues = prestazioneResultModel;
            apiAddAudit.fetch({
              tenantId: tenantId,
              aziendaId: aziendaId,
              area: bsaPrestazione,
              operation: "Create",
              entityType: "prestazione",
              entityId: result.Id.toString(),
              oldValues: "",
              newValues: JSON.stringify(newValues),
            });

            //Aggiungo i professionisti appartenenti alla prestazione
            let professionisti = [];
            currentPrestazione.professionisti.map((item) =>
              professionisti.push({
                PrestazioneId: prestazioneResultModel.id,
                UserId: item.userId,
              })
            );

            const addPresUsersResponse = await apiAddPrestazioneUsers.fetch({
              objects: professionisti,
            });
            if (addPresUsersResponse.data.errors !== undefined) {
              enqueueSnackbar(t("error.generale"), { variant: "error" });
            } else {
              //Aggiorno l'elenco delle prestazioni
              const arrItemsUsers = [];
              professionisti.forEach((itemU) => {
                utentiList.forEach((item2) => {
                  if (itemU.UserId === item2.userId) {
                    arrItemsUsers.push(item2);
                  }
                });
              });
              prestazioneResultModel.professionisti = arrItemsUsers;

              let prestazioni = Object.assign([], currentPrestazioniList);
              prestazioni.push(prestazioneResultModel);
              prestazioni = prestazioni.sort(
                (a, b) =>
                  new Date(...b.dataStart.split("/").reverse()) -
                  new Date(...a.dataStart.split("/").reverse())
              );
              setCurrentPrestazioniList(prestazioni);

              let allPrestazioni = Object.assign([], allPrestazioniList);
              allPrestazioni.push(prestazioneResultModel);
              allPrestazioni = allPrestazioni.sort(
                (a, b) =>
                  new Date(...b.dataStart.split("/").reverse()) -
                  new Date(...a.dataStart.split("/").reverse())
              );
              setAllPrestazioniList(allPrestazioni);
              setCurrentPrestazione(prestazioneResultModel);
              setCurrentPrestazioneOriginal(prestazioneResultModel);
              setCurrentPrestazionePagamento(
                emptyPagamentoModel(prestazioneResultModel, baseValutaList)
              );
              setCurrentPrestazionePagamentoOriginal(
                emptyPagamentoModel(prestazioneResultModel, baseValutaList)
              );
              setCurrentPrestazionePagamentiList([]);
              setCanIShowMezziPag(true);

              enqueueSnackbar(t("message.success"), { variant: "success" });
            }
          }
        }

        //Se la sto modificando
        else {
          const newDateStartD = new Date(currentPrestazione.dataStart);
          const newDateStart = new Date(
            Date.UTC(
              newDateStartD.getFullYear(),
              newDateStartD.getMonth(),
              newDateStartD.getDate(),
              newDateStartD.getHours(),
              newDateStartD.getMinutes(),
              newDateStartD.getSeconds()
            )
          );

          let newDateEnd = null;
          if (currentPrestazione.dataEnd !== null) {
            const newDateEndD = new Date(currentPrestazione.dataEnd);
            newDateEnd = new Date(
              Date.UTC(
                newDateEndD.getFullYear(),
                newDateEndD.getMonth(),
                newDateEndD.getDate(),
                newDateEndD.getHours(),
                newDateEndD.getMinutes(),
                newDateEndD.getSeconds()
              )
            );
          }

          const updatePresResponse = await apiUpdatePrestazione.fetch({
            id: currentPrestazione.id,
            tipoKey: currentPrestazione.tipoKey,
            prestazioneId: currentPrestazione.prestazioneId,
            naturaId: currentPrestazione.naturaId,
            scopoId: currentPrestazione.scopoId,
            startDate: newDateStart,
            endDate: newDateEnd,
            paeseId: currentPrestazione.paeseId,
            provinciaId: currentPrestazione.provinciaId,
            provincia: currentPrestazione.provincia,
            cittaId: currentPrestazione.cittaId,
            citta: currentPrestazione.citta,
            descrizione: currentPrestazione.descrizione !== "" ? currentPrestazione.descrizione : "Non indicata",
            isPrivate: currentPrestazione.isPrivate,
            astensioneMotivo: currentPrestazione.astensioneMotivo,
            isEsoneroObbAst: currentPrestazione.isEsoneroObbAst,
            settoreAttivita: currentPrestazione.settoreAttivita,
            settorePrestazione: currentPrestazione.settorePrestazione,
            ragione: currentPrestazione.ragione, 
          });
          if (updatePresResponse.data.errors !== undefined) {
            enqueueSnackbar(t("error.generale"), { variant: "error" });
          } else {
            const result = updatePresResponse.data.data.update_prestazione.returning[0];
            let prestazioneResultModel = createPrestazioneModel(result)

            //Aggiungo la Audit per tracciare l'azione
            const newValues = prestazioneResultModel;
            apiAddAudit.fetch({
              tenantId: tenantId,
              aziendaId: aziendaId,
              area: bsaPrestazione,
              operation: "Update",
              entityType: "prestazione",
              entityId: currentPrestazione.id.toString(),
              oldValues: JSON.stringify(currentPrestazioneOriginal),
              newValues: JSON.stringify(newValues),
            });

            //Rimuovo i professionisti appartenenti al fascicolo
            const delPresUsersResponse = await apiDeletePrestazioneUser.fetch({
              prestazioneId: currentPrestazione.id,
            });
            if (delPresUsersResponse.data.errors !== undefined) {
              enqueueSnackbar(t("error.generale"), { variant: "error" });
            } else {
              //e li aggiungo di nuovo
              let professionisti = [];
              currentPrestazione.professionisti.map((item) =>
                professionisti.push({
                  PrestazioneId: currentPrestazione.id,
                  UserId: item.userId,
                })
              );

              const addPresUsersResponse = await apiAddPrestazioneUsers.fetch({
                objects: professionisti,
              });
              if (addPresUsersResponse.data.errors !== undefined) {
                enqueueSnackbar(t("error.generale"), { variant: "error" });
              } else {
                //Aggiorno l'elenco delle prestazioni
                const arrItemsUsers = [];
                professionisti.forEach((itemU) => {
                  utentiList.forEach((item2) => {
                    if (itemU.UserId === item2.userId) {
                      arrItemsUsers.push(item2);
                    }
                  });
                });
                prestazioneResultModel.professionisti = arrItemsUsers;
                prestazioneResultModel.pagamenti = currentPrestazione.pagamenti;

                let prestazioni = Object.assign([], currentPrestazioniList);
                const index = prestazioni.findIndex(
                  (item) => item.id === prestazioneResultModel.id
                );
                prestazioni[index] = prestazioneResultModel;
                prestazioni = prestazioni.sort(
                  (a, b) =>
                    new Date(...b.dataStart.split("/").reverse()) -
                    new Date(...a.dataStart.split("/").reverse())
                );

                setCurrentPrestazioniList(prestazioni);

                let allPrestazioni = Object.assign([], allPrestazioniList);
                const allIndex = allPrestazioni.findIndex(
                  (item) => item.id === prestazioneResultModel.id
                );
                allPrestazioni[allIndex] = prestazioneResultModel;
                allPrestazioni = allPrestazioni.sort(
                  (a, b) =>
                    new Date(...b.dataStart.split("/").reverse()) -
                    new Date(...a.dataStart.split("/").reverse())
                );
                setAllPrestazioniList(allPrestazioni);
                setCurrentPrestazione(prestazioneResultModel);
                setCurrentPrestazioneOriginal(prestazioneResultModel);
                setCurrentPrestazionePagamento(
                  emptyPagamentoModel(prestazioneResultModel, baseValutaList)
                );
                setCurrentPrestazionePagamentoOriginal(
                  emptyPagamentoModel(prestazioneResultModel, baseValutaList)
                );

                enqueueSnackbar(t("message.success"), { variant: "success" });
              }
            }

            setCurrentPrestazioneOriginal(currentPrestazione);
            setCanIShowMezziPag(true);
          }
        }

        //Aggiorno la data di ultima modifica del fascicolo
        fascicolo.tenantId = tenantId;
        fascicolo.aziendaId = aziendaId;
        let updatedFascicolo = await fascicoloApi.UpdateFascicolo(fascicolo,fascicolo.isPrivate)
        if(!updatedFascicolo){
          enqueueSnackbar("Errore durante l'aggiornamento del fascicolo",{variant:"error"})
          return
        }        
      } else {
        enqueueSnackbar(t("error.missingFields"), { variant: "error" });
      }
    } catch (e) {
      logApi.AddLog(e.message + "Prestazione: "+ currentPrestazione,e.stack,"HandleSaveClick","PrestazioneForm")      
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    } finally {
      setBackDropOpen(false);
      reloadStatusFascicolo();
    }
  };

  //Salvataggio degli inputs Mezzi di pagamento
  const handlePagamentoDate = (date) => {
    if (date !== null && isDateValid(date)) {
      const newDate = new Date(date);
      currentPrestazionePagamento.data = newDate;
      setCurrentPrestazionePagamento((prevState) => ({
        ...prevState,
        data: newDate,
        valutaCambioData: newDate,
      }));
    }
  };
  async function evaluateImporto(isoCode) {
    if (isoCode && currentPrestazionePagamento.valutaImporto > 0) {
      try {
        const conversionResponse = await currencyApi.convertToEur(
          isoCode,
          currentPrestazionePagamento.valutaImporto,
          currentPrestazionePagamento.valutaCambioData
        );
        return conversionResponse;
      } catch (error) {
        console.error("Errore durante la conversione valuta:", error);
        return null;
      }
    }
    return null;
  };
  const manualConversion = (tassoCambio, valutaImporto) => {
    if (tassoCambio !== undefined && tassoCambio > 0 && valutaImporto > 0) {
      return valutaImporto * tassoCambio;
    }
    return 0;
  };
  const evaluateAmountThresold = () => {
    if (
      currentPrestazionePagamento.modalitaPagamentoId !== undefined &&
      currentPrestazionePagamento.modalitaPagamentoId > 0
    ) {
      const modalita = baseMezzoPagamentoList.filter(
        (item) => item.id === currentPrestazionePagamento.modalitaPagamentoId
      )[0];

      if (
        modalita.key === "base_mezzo_pagamento_1" &&
        currentPrestazionePagamento.importo > sogliaPagamentoContanti
      ) {
        enqueueSnackbar(
          t("prestazione.mezzoPagamento.error.singleAmountMoreThanThreshold"),
          { variant: "error" }
        );
      }

      let amountContantiSum = 0;
      currentPrestazione.pagamenti.forEach((item) => {
        const modalita2 = baseMezzoPagamentoList.filter(
          (baseMP) => baseMP.id === item.modalitaPagamentoId
        )[0];
        if (modalita2.key === "base_mezzo_pagamento_1") {
          amountContantiSum = amountContantiSum + item.importo;
        }
      });

      if (amountContantiSum > sogliaPagamentoContanti) {
        enqueueSnackbar(
          t("prestazione.mezzoPagamento.error.sumAmountMoreThanThreshold"),
          { variant: "error" }
        );
      }
    }
  };
  const handleChangePagamento = async (event, value) => {
    const { id } = event.target

    if (id.indexOf("ddlValuta-option") !== -1 || id === "ddlValuta") {
      const valutaItem = baseValutaList.filter((item) => item.isoCode === value.isoCode)[0];
      setIsValutaEstera(valutaItem.isoCode !== "EUR");
      currentPrestazionePagamento.valutaId = valutaItem.id;
      currentPrestazionePagamento.isoCode = valutaItem.isoCode;      
      setValutaByIso(valutaItem.isoCode);
      
      // Svuota il campo importo quando cambia la valuta
      currentPrestazionePagamento.valutaImporto = 0;
      currentPrestazionePagamento.importo = 0;
      currentPrestazionePagamento.valutaCambio = 0;
      setPagamentoImportoEuro(0);
      setValutaCambio(0);
      setValutaCambioInput("");
      if (txtImportoRef.current) {
        txtImportoRef.current.value = "";
      }
    } else if (id === "txtValutaCambio") {
      // Salviamo il valore grezzo durante la digitazione
      setValutaCambioInput(event.target.value);
    } else if (
      id.indexOf("ddlMezzoPagamento-option") !== -1 ||
      id === "ddlMezzoPagamento"
    ) {
      currentPrestazionePagamento.modalitaPagamentoId = value.id;
      evaluateAmountThresold();
    } else if (
      id.indexOf("ddlOrigineFondi-option") !== -1 ||
      id === "ddlOrigineFondi"
    ) {
      currentPrestazionePagamento.origineId = value.id;
    } else if (id === "chbShowMezziPag") {
      setShowMezziPag(event.target.checked);
    } else if( id === "txtImporto"){
      currentPrestazionePagamento.importo = event.target.value;
    }

    setCurrentPrestazionePagamento({ ...currentPrestazionePagamento });
  };
  const handleBlurImportoPagamento = async (event, value) => {
    const { id } = event.target;

    if (id === "txtImporto") {
      // Parse del valore inserito dall'utente
      const importoValuta = event.target.value;
      const importoPulito = importoValuta.replace(".", "").replace(",", ".");
      const importoParsed = parseFloat(importoPulito);
      // Valida che sia un numero valido
      if (isNaN(importoParsed) || importoParsed <= 0) {
        enqueueSnackbar(
          "Inserire un importo valido",
          { variant: "warning" }
        );
        return;
      }
      // Aggiorna il valore parsato
      currentPrestazionePagamento.valutaImporto = importoParsed;
      // Ottieni l'isoCode della valuta selezionata
      const valutaItem = baseValutaList.find(
        (item) => item.isoCode === currentPrestazionePagamento.isoCode
      );
      if (!valutaItem || !valutaItem.isoCode) {
        console.error("Valuta non trovata");
        return;
      }
      // Chiama l'API per la conversione
      const conversionResponse = await evaluateImporto(valutaItem.isoCode);
      currentPrestazionePagamento.valutaId = conversionResponse?.valutaId ?? null;            
      if (conversionResponse) {
        // Aggiorna i valori con la risposta dell'API
        currentPrestazionePagamento.valutaCambio = conversionResponse.exchangeRate;
        currentPrestazionePagamento.importo = conversionResponse.convertedAmount;        
        setIsValutaEstera(valutaItem.isoCode !== "EUR");
        setValutaCambio(conversionResponse.exchangeRate);
        setValutaCambioInput(conversionResponse.exchangeRate.toLocaleString("it-IT", {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }));
        setPagamentoImportoEuro(conversionResponse.convertedAmount);
      } else {
        // In caso di errore API, prova con conversione manuale se esiste un cambio
        if (currentPrestazionePagamento.valutaCambio > 0) {
          const importoCalcolato = manualConversion(
            currentPrestazionePagamento.valutaCambio,
            currentPrestazionePagamento.valutaImporto
          );
          currentPrestazionePagamento.importo = importoCalcolato;
          setPagamentoImportoEuro(importoCalcolato);
        } else {
          // Nessun cambio disponibile
          enqueueSnackbar(
            "Impossibile recuperare il tasso di cambio. Inseriscilo manualmente.",
            { variant: "warning" }
          );
        }
      }      
      // Aggiorna lo stato
      setCurrentPrestazionePagamento({ ...currentPrestazionePagamento });      
      // Esegui le validazioni
      evaluateAmountThresold();
    }
  };
  const handleBlurValutaCambio = (event, value) => {
    const { id } = event.target;

    if (id === "txtValutaCambio" && event.target.value !== "") {
      const cambio = event.target.value.replace(",",".");
      const cambioNumber = parseFloat(cambio);
      
      if (isNaN(cambioNumber) || cambioNumber <= 0) {
        enqueueSnackbar(
          "Inserire un tasso di cambio valido",
          { variant: "warning" }
        );
        return;
      }
      
      // Aggiorna gli stati con il valore parsato e formattato
      setValutaCambio(cambioNumber);
      setValutaCambioInput(cambioNumber.toLocaleString("it-IT", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }));
      
      currentPrestazionePagamento.valutaCambio = cambioNumber;
      let importo = manualConversion(cambioNumber, currentPrestazionePagamento.valutaImporto);
      currentPrestazionePagamento.importo = importo;
      setPagamentoImportoEuro(importo);
      setCurrentPrestazionePagamento({ ...currentPrestazionePagamento });
      evaluateAmountThresold();
    }
  };
  const handleValutaCambioDate = (date) => {
    const newDate = new Date(date);
    currentPrestazionePagamento.valutaCambioData = newDate;       
    // Svuota il campo importo quando cambia la data
    currentPrestazionePagamento.valutaImporto = 0;
    currentPrestazionePagamento.importo = 0;
    currentPrestazionePagamento.valutaCambio = 0;
    setPagamentoImportoEuro(0);
    setValutaCambio(0);
    if (txtImportoRef.current) {
      txtImportoRef.current.value = "";
    }
    setCurrentPrestazionePagamento((prevState) => ({
      ...prevState,
      valutaCambioData: newDate
    }));
  };
  //#endregion

  //CRUD del Mezzo di Pagamento
  // valutaId/valutaCambio are only known after convertToEur (baseValutaModel has neither)
  const emptyPagamentoModel = (prestazione, valute) => {
    setIsValutaEstera(false);
    setValutaCambio(1);

    let dataPagamento = new Date();
    if (prestazione !== null && prestazione.id > 0) {
      dataPagamento = prestazione.dataStart;
    }

    const newItem = new prestazionePagamentoModel(
      0,
      null,
      dataPagamento,
      0,
      0,
      0,
      undefined,
      undefined,
      null,
      null,
      "",
      "",
      "",
      dataPagamento,
      loggedUserId,
      null,
      "EUR",
    );
    return newItem;
  };

  //#region Handler Pagamento
  const handleCancelPagamentoClick = () => {
    setCurrentPrestazionePagamento(emptyPagamentoModel(currentPrestazione, baseValutaList));
    setCurrentPrestazionePagamentoOriginal(
      emptyPagamentoModel(currentPrestazione, baseValutaList)
    );
    setPagamentoImportoEuro(0);
    setValutaCambio(0);
    setValutaCambioInput("");
    setShowMezziPag(currentPrestazionePagamentiList.length > 0);
  };
  const handleSavePagamentoClick = async () => {
    try {
      if (
        currentPrestazionePagamento.importo !== undefined &&
        currentPrestazionePagamento.modalitaPagamentoId !== undefined &&
        currentPrestazionePagamento.modalitaPagamentoId > 0 &&
        currentPrestazionePagamento.origineId !== undefined &&
        currentPrestazionePagamento.origineId > 0 &&
        currentPrestazionePagamento.isoCode !== undefined &&
        currentPrestazionePagamento.isoCode !== "" &&
        currentPrestazionePagamento.valutaImporto > 0
      ) {
        // Save may be clicked before the txtImporto blur conversion has resolved
        if (
          !(currentPrestazionePagamento.valutaCambio > 0) ||
          currentPrestazionePagamento.valutaId === null ||
          currentPrestazionePagamento.valutaId === undefined
        ) {
          const conversion = await evaluateImporto(
            currentPrestazionePagamento.isoCode
          );
          if (conversion) {
            currentPrestazionePagamento.valutaId = conversion.valutaId;
            currentPrestazionePagamento.valutaCambio = conversion.exchangeRate;
            currentPrestazionePagamento.importo = conversion.convertedAmount;
            setPagamentoImportoEuro(conversion.convertedAmount);
          }
        }
        if (
          !(currentPrestazionePagamento.valutaCambio > 0) ||
          currentPrestazionePagamento.valutaId === null ||
          currentPrestazionePagamento.valutaId === undefined ||
          !(currentPrestazionePagamento.importo > 0)
        ) {
          enqueueSnackbar(
            "Impossibile recuperare il tasso di cambio. Reinserire l'importo e riprovare.",
            { variant: "error" }
          );
          return;
        }

        //Se sto creando un mezzo di pagamento
        if (currentPrestazionePagamento.id === 0) {
          const newDateD = new Date(currentPrestazionePagamento.data);
          const newDate = new Date(
            Date.UTC(
              newDateD.getFullYear(),
              newDateD.getMonth(),
              newDateD.getDate(),
              newDateD.getHours(),
              newDateD.getMinutes(),
              newDateD.getSeconds()
            )
          );

          const addPresPagResponse = await apiAddPrestazionePagamento.fetch({
            prestazioneId: currentPrestazione.id,
            valutaId: currentPrestazionePagamento.valutaId ?? null,
            valutaData: currentPrestazionePagamento.valutaCambioData,
            valutaCambio: currentPrestazionePagamento.valutaCambio,
            valutaImporto: currentPrestazionePagamento.valutaImporto,
            modalitaPagamentoId: currentPrestazionePagamento.modalitaPagamentoId,
            origineId: currentPrestazionePagamento.origineId,
            importoEuro: currentPrestazionePagamento.importo,
            data: newDate,
            isoCode : currentPrestazionePagamento.isoCode
          });
          if (addPresPagResponse.data.errors !== undefined) {
            enqueueSnackbar(t("error.generale"), { variant: "error" });
          } else {
            const result =
              addPresPagResponse.data.data.insert_prestazione_pagamento_one;
            const prestazionePagamentoResultModel =
              new prestazionePagamentoModel(
                result.Id,
                result.ValutaId,
                result.ValutaData,
                result.ValutaCambio,
                result.ValutaImporto,
                result.ImportoEuro,
                result.ModalitaPagamentoId,
                result.OrigineId,
                result.CreatedOn,
                result.ModifiedOn,
                "",
                result.base_mezzo_pagamento.Value,
                result.base_origine_fondi.Value,
                result.Data,
                result.CreatedBy,
                result.ModifiedBy,
                result.IsoCode
              );

            //Aggiungo la Audit per tracciare l'azione
            const newValues = prestazionePagamentoResultModel;
            apiAddAudit.fetch({
              tenantId: tenantId,
              aziendaId: aziendaId,
              area: bsaPrestazione,
              operation: "Create",
              entityType: "mezzo_pagamento",
              entityId: result.Id.toString(),
              oldValues: "",
              newValues: JSON.stringify(newValues),
            });

            const pagamenti = Object.assign([], currentPrestazione.pagamenti);
            pagamenti.push(prestazionePagamentoResultModel);
            currentPrestazione.pagamenti = pagamenti;
            setCurrentPrestazione(currentPrestazione);
            setCurrentPrestazioneOriginal(currentPrestazione);
            setCurrentPrestazionePagamento(
              emptyPagamentoModel(currentPrestazione, baseValutaList)
            );
            setCurrentPrestazionePagamentoOriginal(
              emptyPagamentoModel(currentPrestazione, baseValutaList)
            );
            setPagamentoImportoEuro(0);
            setValutaCambio(0);
            setCurrentPrestazionePagamentiList(pagamenti);
            evaluateTipoPrestazione(
              currentPrestazione.professionisti,
              pagamenti
            );

            enqueueSnackbar(t("message.success"), { variant: "success" });
          }
        }

        //Se lo sto modificando
        else {
          const newDateD = new Date(currentPrestazionePagamento.data);
          const newDate = new Date(
            Date.UTC(
              newDateD.getFullYear(),
              newDateD.getMonth(),
              newDateD.getDate(),
              newDateD.getHours(),
              newDateD.getMinutes(),
              newDateD.getSeconds()
            )
          );

          const updatePresPagResponse = await apiUpdatePrestazionePagamento.fetch({
              id: currentPrestazionePagamento.id,
              valutaId: currentPrestazionePagamento.valutaId ?? null,
              valutaData: currentPrestazionePagamento.valutaCambioData,
              valutaCambio: currentPrestazionePagamento.valutaCambio,
              valutaImporto: currentPrestazionePagamento.valutaImporto,
              modalitaPagamentoId: currentPrestazionePagamento.modalitaPagamentoId,
              origineId: currentPrestazionePagamento.origineId,
              importoEuro: currentPrestazionePagamento.importo,
              data: newDate,
              isoCode : currentPrestazionePagamento.isoCode
            });
          if (updatePresPagResponse.data.errors !== undefined) {
            enqueueSnackbar(t("error.generale"), { variant: "error" });
          } else {
            const result =
              updatePresPagResponse.data.data.update_prestazione_pagamento
                .returning[0];
            const prestazionePagamentoResultModel =
              new prestazionePagamentoModel(
                result.Id,
                result.ValutaId,
                result.ValutaData,
                result.ValutaCambio,
                result.ValutaImporto,
                result.ImportoEuro,
                result.ModalitaPagamentoId,
                result.OrigineId,
                result.CreatedOn,
                result.ModifiedOn,
                "",
                result.base_mezzo_pagamento.Value,
                result.base_origine_fondi.Value,
                result.Data,
                result.CreatedBy,
                result.ModifiedBy,
                result.IsoCode
              );

            //Aggiungo la Audit per tracciare l'azione
            const newValues = prestazionePagamentoResultModel;
            apiAddAudit.fetch({
              tenantId: tenantId,
              aziendaId: aziendaId,
              area: bsaPrestazione,
              operation: "Update",
              entityType: "mezzo_pagamento",
              entityId: currentPrestazionePagamento.id.toString(),
              oldValues: JSON.stringify(currentPrestazionePagamentoOriginal),
              newValues: JSON.stringify(newValues),
            });

            const pagamenti = Object.assign([], currentPrestazione.pagamenti);
            const index = pagamenti.findIndex(
              (item) => item.id === prestazionePagamentoResultModel.id
            );
            pagamenti[index] = prestazionePagamentoResultModel;
            currentPrestazione.pagamenti = pagamenti;

            setCurrentPrestazione(currentPrestazione);
            setCurrentPrestazioneOriginal(currentPrestazione);
            setCurrentPrestazionePagamento(
              emptyPagamentoModel(currentPrestazione, baseValutaList)
            );
            setCurrentPrestazionePagamentoOriginal(
              emptyPagamentoModel(currentPrestazione, baseValutaList)
            );
            setPagamentoImportoEuro(0);
            setValutaCambio(0);
            setCurrentPrestazionePagamentiList(pagamenti);
            evaluateTipoPrestazione(
              currentPrestazione.professionisti,
              pagamenti
            );

            enqueueSnackbar(t("message.success"), { variant: "success" });
          }
        }

        //Aggiorno la data di ultima modifica del fascicolo
        fascicolo.aziendaId = aziendaId;
        fascicolo.tenantId = tenantId;
        let updatedFascicolo = await fascicoloApi.UpdateFascicolo(fascicolo,fascicolo.isPrivate)
        if(!updatedFascicolo){
          enqueueSnackbar("Errore durante l'aggiornamento del fascicolo",{variant:"error"})
          return;
        }
      } else {
        enqueueSnackbar(t("error.missingFields"), { variant: "error" });
      }
    } catch (e) {
      logApi.AddLog(e.message + " Pagamento: " + currentPrestazionePagamento, e.stack, "HandleSavePagamentoClick","PrestazioneForm")      
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    } finally {
      setBackDropOpen(false);
      reloadStatusFascicolo();
    }
  };
  const handleSelectedPagamentoClick = (item) => {    
    setValutaCambio(item.valutaCambio);
    setValutaCambioInput(item.valutaCambio.toLocaleString("it-IT", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }));
    setCurrentPrestazionePagamento(item);
    setCurrentPrestazionePagamentoOriginal(item);
    setPagamentoImportoEuro(item.importo);
    setIsValutaEstera(item.isoCode !== "EUR");
  };
  const handleDeletePagamentoClick = (item) => {
    setIdToPerform(item.id);
    setTypeToPerform("MP");
    setShowDeleteModal(true);
  };
  //#endregion

  //#region Modal Delete
  const handleModalDeleteClose = () => {
    setIdToPerform(0);
    setTypeToPerform("P");
    setShowDeleteModal(false);
  };
  const handleModalDeleteOkClick = async () => {
    try {
      if (idToPerform > 0) {
        setBackDropOpen(true);

        if (typeToPerform === "P") {
          const delPreResponse = await apiDeletePrestazione.fetch({
            id: idToPerform,
          });
          if (delPreResponse.data.errors !== undefined) {
            enqueueSnackbar(t("error.generale"), { variant: "error" });
          } else {
            //Aggiungo la Audit per tracciare l'azione
            const itemById = JSON.stringify(
              currentPrestazioniList.filter(
                (item) => item.id === idToPerform
              )[0]
            );
            apiAddAudit.fetch({
              tenantId: tenantId,
              aziendaId: aziendaId,
              area: bsaPrestazione,
              operation: "Delete",
              entityType: "prestazione",
              entityId: idToPerform.toString(),
              oldValues: itemById,
              newValues: "",
            });

            //Aggiorno l'elenco delle prestazioni
            const prestazioni = Object.assign([], currentPrestazioniList);
            const newPrestazioni = prestazioni.filter(
              (item) => item.id !== idToPerform
            );
            setCurrentPrestazioniList(newPrestazioni);

            const allPrestazioni = Object.assign([], allPrestazioniList);
            const newAllPrestazioni = allPrestazioni.filter(
              (item) => item.id !== idToPerform
            );
            setAllPrestazioniList(newAllPrestazioni);
            setCurrentPrestazione(emptyPrestazioneModel());
            setCurrentPrestazioneOriginal(emptyPrestazioneModel());
            setCurrentPrestazionePagamento(
              emptyPagamentoModel(null, baseValutaList)
            );
            setCurrentPrestazionePagamentoOriginal(
              emptyPagamentoModel(null, baseValutaList)
            );
            setCurrentPrestazioneChbVal({ isPrivate: false });
            setCurrentPrestazionePagamentiList([]);
            setShowMezziPag(false);
            setCanIShowMezziPag(false);
            setShowPrestazioneForm(false);

            enqueueSnackbar(t("message.success"), { variant: "success" });
          }
        } else if (typeToPerform === "MP") {
          const delPresPagResponse = await apiDeletePrestazionePagamento.fetch({
            id: idToPerform,
          });
          if (delPresPagResponse.data.errors !== undefined) {
            enqueueSnackbar(t("error.generale"), { variant: "error" });
          } else {
            //Aggiungo la Audit per tracciare l'azione
            const itemById = JSON.stringify(
              currentPrestazione.pagamenti.filter(
                (item) => item.id === idToPerform
              )[0]
            );
            apiAddAudit.fetch({
              tenantId: tenantId,
              aziendaId: aziendaId,
              area: bsaPrestazione,
              operation: "Delete",
              entityType: "mezzo_pagamento",
              entityId: idToPerform.toString(),
              oldValues: itemById,
              newValues: "",
            });

            //Aggiorno l'elenco dei mezzi di pagamento per la prestazione corrente
            const pagamenti = Object.assign([], currentPrestazione.pagamenti);
            const newPagamenti = pagamenti.filter(
              (item) => item.id !== idToPerform
            );
            currentPrestazione.pagamenti = newPagamenti;
            setCurrentPrestazione(currentPrestazione);
            setCurrentPrestazionePagamento(
              emptyPagamentoModel(currentPrestazione, baseValutaList)
            );
            setCurrentPrestazionePagamentoOriginal(
              emptyPagamentoModel(currentPrestazione, baseValutaList)
            );
            setCurrentPrestazionePagamentiList(newPagamenti);
            setPagamentoImportoEuro(0);
            setValutaCambio(0);
            evaluateTipoPrestazione(
              currentPrestazione.professionisti,
              newPagamenti
            );
            setShowMezziPag(newPagamenti.length > 0);

            enqueueSnackbar(t("message.success"), { variant: "success" });
          }
        }

        //Aggiorno la data di ultima modifica del fascicolo
        fascicolo.aziendaId = aziendaId;
        fascicolo.tenantId = tenantId;
        let updatedFascicolo = await fascicoloApi.UpdateFascicolo(fascicolo,fascicolo.isPrivate)
        if(!updatedFascicolo){
          enqueueSnackbar("Errore durante l'aggiornamento del fascicolo",{variant:"error"})
          return;
        }
      }
    } catch (e) {
      logApi.AddLog(e.message+ "Prestazione Id: " +idToPerform,e.stack,"HandleModalDeleteOkClick","PrestazioneForm")
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    } finally {
      setIdToPerform(0);
      setTypeToPerform("P");
      setShowDeleteModal(false);
      setBackDropOpen(false);
      reloadStatusFascicolo();
    }
  };
  //#endregion

  //EuroConference
  const handleEuroConfClick = async (itemId) => {
    try {
      setBackDropOpen(true);
      const openUrl = await euroConferenceApi.BuildEuroConferenceUrl(itemId);
      window.open(openUrl, "_blank", "noopener,noreferrer");      
    } catch (e) {
      logApi.AddLog(e.message,e.stack,"HandleEuroConfClick","PrestazioneForm")      
      enqueueSnackbar(t("error.generale"), { variant: "error" });
    } finally {
      setBackDropOpen(false);
    }
  };

  //#region Return graphics
  return (
    <Fragment>
      <VaporPage.Section>
        <Loading open={backDropOpen} />

        {isDataLoaded && !userPermissions.canRead ? <NotAuth /> : null}

        {showDeleteModal && idToPerform > 0 && typeToPerform !== "" ? (
          <ModalDelete
            show={showDeleteModal}
            onClose={handleModalDeleteClose}
            onOk={handleModalDeleteOkClick}            
            deleteMessage={t("modal.deleteDesc")}
          ></ModalDelete>
        ) : null}

        {isDataLoaded && userPermissions.canRead ? (
          <Grid container>
            <Grid item xs={12}>
              <Stack
                direction="row"
                spacing={2}
                divider={<Divider orientation="vertical" flexItem />}
              >
                <Stack direction="column" spacing={2} sx={{ width: "25%" }}>
                  {userPermissions.canCreate && !showAllPrestazioni ? (
                    <Button
                      variant="contained"
                      size="small"
                      endIcon={<AddIcon />}
                      onClick={handleAddPrestClick}
                    >
                      {t("prestazione.titoloAdd")}
                    </Button>
                  ) : null}
                  <ToggleButtonGroup
                    value={showAllPrestazioni ? "all" : "mine"}
                    exclusive
                    onChange={(e, val) => { if (val !== null) { setShowAllPrestazioni(val === "all"); setShowPrestazioneForm(false); setIsPrestazioneReadOnly(false); setCurrentPrestazione(emptyPrestazioneModel()); setCanIShowMezziPag(false); } }}
                    size="small"
                    fullWidth
                  >
                    <ToggleButton value="mine">
                      {tenantTypeId === 1 ? "Commercialista" : tenantTypeId === 2 ? "Cons. lavoro" : tenantTypeId === 3 ? "Avvocato" : (t("prestazione.leMie") || "Le mie")}
                    </ToggleButton>
                    <ToggleButton value="all">
                      {`Altri professionisti (${otherPrestazioniCount})`}
                    </ToggleButton>
                  </ToggleButtonGroup>
                  {/* Filtro secondario per tipo tenant - nascosto in PROD finché non ci saranno utenti avvocati */}
                  {showAllPrestazioni && (
                    <ToggleButtonGroup
                      value={filterTipoTenantId || null}
                      exclusive
                      onChange={(e, val) => { setFilterTipoTenantId(val || 0); setShowPrestazioneForm(false); setIsPrestazioneReadOnly(false); setCurrentPrestazione(emptyPrestazioneModel()); setCanIShowMezziPag(false); }}
                      size="small"
                      fullWidth
                      sx={{ display: "none" }}
                    >
                      {tenantTypeId !== 1 && (
                      <ToggleButton value={1}>
                        <Tooltip title="Commercialista"><AccountBalanceIcon fontSize="small" /></Tooltip>
                      </ToggleButton>
                      )}
                      {tenantTypeId !== 2 && (
                      <ToggleButton value={2}>
                        <Tooltip title="Consulente del lavoro"><WorkIcon fontSize="small" /></Tooltip>
                      </ToggleButton>
                      )}
                      {tenantTypeId !== 3 && (
                      <ToggleButton value={3}>
                        <Tooltip title="Avvocato"><GavelIcon fontSize="small" /></Tooltip>
                      </ToggleButton>
                      )}
                    </ToggleButtonGroup>
                  )}
                  {(showAllPrestazioni ? allPrestazioniList.filter(p => p.tipoTenantId !== tenantTypeId) : currentPrestazioniList).length > 0 ? (
                    <TableContainer component={Paper}>
                      <Table size="small" sx={{ mt: 2 }}>
                        <TableBody>
                          {(showAllPrestazioni ? allPrestazioniList.filter(p => p.tipoTenantId !== tenantTypeId) : currentPrestazioniList).map((item) => (
                            <TableRow
                              key={uuidv4()}
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                              hover
                              selected={currentPrestazione?.id === item.id}
                              style={item.dataEnd !== null && new Date(item.dataEnd) <= new Date() 
                                ? {opacity : 0.5} 
                                : {}
                              }
                            >
                              <TableCell
                                key={uuidv4()}
                                component="th"
                                scope="row"
                              >
                                <Stack
                                  direction="row"
                                  justifyContent="center"
                                  alignItems="center"
                                >
                                  <Stack direction="column" sx={{ flexGrow: 1 }}>
                                    <ExtendedTypography
                                      variant="titleSmall"
                                      color={item.tipoTenantId === tenantTypeId ? "primary.main" : "text.secondary"}
                                      fontStyle={{ fontWeight: "bold" }}
                                    >                                  
                                      {item.prestazioneNome}                                           
                                    </ExtendedTypography>
                                    <ExtendedTypography
                                      variant="subtitle"
                                      color="primary"
                                      fontStyle={{ fontWeight: "bold" }}
                                    >
                                      {new Date(
                                        item.dataStart
                                      ).toLocaleDateString()}
                                    </ExtendedTypography>
                                    <ExtendedTypography
                                      variant="body"
                                      color="text.secondary"
                                    >                        
                                      {item.descrizione !== "" ? item.descrizione : "{Nessuna descrizione}"}                                                    
                                    </ExtendedTypography>
                                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                                      <Tooltip title={
                                        item.tipoTenantId === 1 ? "Commercialista" :
                                        item.tipoTenantId === 2 ? "Consulente del lavoro" :
                                        item.tipoTenantId === 3 ? "Avvocato" : ""
                                      }>
                                        {item.tipoTenantId === 1 ? (
                                          <AccountBalanceIcon sx={{ fontSize: "0.875rem" }} color={item.tipoTenantId === tenantTypeId ? "primary" : "disabled"} />
                                        ) : item.tipoTenantId === 2 ? (
                                          <WorkIcon sx={{ fontSize: "0.875rem" }} color={item.tipoTenantId === tenantTypeId ? "primary" : "disabled"} />
                                        ) : item.tipoTenantId === 3 ? (
                                          <GavelIcon sx={{ fontSize: "0.875rem" }} color={item.tipoTenantId === tenantTypeId ? "primary" : "disabled"} />
                                        ) : null}
                                      </Tooltip>
                                      <ExtendedTypography
                                        variant="body"
                                        color="text.disabled"
                                        sx={{ fontSize: "0.75rem" }}
                                      >
                                        {item.tipoTenantId === 1 ? "Commercialista" :
                                         item.tipoTenantId === 2 ? "Cons. lavoro" :
                                         item.tipoTenantId === 3 ? "Avvocato" : ""}
                                      </ExtendedTypography>
                                    </Stack>
                                    {item.professionisti && item.professionisti.length > 0 && (
                                      <ExtendedTypography
                                        variant="body"
                                        color="text.disabled"
                                        sx={{ fontSize: "0.75rem", mt: 0.5 }}
                                      >
                                        {"Erogata da"}: {item.professionisti.map((prof) => prof.completeName).join(", ")}
                                      </ExtendedTypography>
                                    )}
                                  </Stack>
                                  <Box sx={{ flexGrow: 1 }} />
                                  <Stack direction="row">
                                    {item.tipoTenantId !== tenantTypeId ? (
                                      <IconButton
                                        edge="end"
                                        aria-label="view"
                                        color="default"
                                        onClick={() =>
                                          handleViewPrestClick(item)
                                        }
                                        sx={{ mr: "1px !important" }}
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    ) : (
                                      <Fragment>
                                    {(!item.isPrivate ||
                                      item.professionisti
                                        .map((prof) => prof.userId)
                                        .includes(loggedUserId)) && (
                                      <IconButton
                                        edge="end"
                                        aria-label="select"
                                        color="primary"
                                        onClick={() =>
                                          handleSelectedPrestClick(item)
                                        }
                                        sx={{ mr: "1px !important" }}
                                      >
                                        <ModeEditIcon fontSize="small" />
                                      </IconButton>
                                    )}                                    

                                    {userPermissions.canDelete &&
                                    (!item.isPrivate ||
                                      item.professionisti
                                        .map((prof) => prof.userId)
                                        .includes(loggedUserId)) ? (
                                      <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        color="error"
                                        onClick={() =>
                                          handleDeletePrestClick(item)
                                        }
                                        sx={{ mr: "1px !important" }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    ) : null}
                                      </Fragment>
                                    )}
                                  </Stack>
                                </Stack>
                                {item.tipoTenantId === tenantTypeId && (
                                <Fragment>
                                <Divider
                                  variant="standard"
                                  sx={{ mt: 1, mb: 1 }}
                                />
                                <ExtendedTypography
                                  variant="titleSmall"
                                  color="primary.main"                                  
                                  fontStyle={{ fontWeight: "bold" }}
                                >
                                    Valutazioni del rischio 
                                </ExtendedTypography>
                                <Divider
                                  variant="standard"
                                  sx={{ mt: 1, mb: 1 }}
                                />
                                {item.professionisti.map((itemP) => (
                                  <Stack
                                    key={uuidv4()}
                                    direction="column"
                                    justifyContent="center"
                                    alignItems="left"
                                  >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                      <Stack direction="column" sx={{ flexGrow: 1 }}>
                                        <ExtendedTypography
                                          variant="body"
                                          color="text.secondary"                                          
                                        >
                                          {t("prestazione.professionista")}{" "}
                                          <strong>{itemP.completeName}</strong>
                                        </ExtendedTypography>
                                        <ExtendedTypography
                                          variant="body"
                                          color="text.secondary"
                                          
                                        >
                                          {t("prestazione.valutazioneRischi")}{" "}
                                          <strong>{item.rischi.filter(
                                            (x) => x.ProvidedBy === itemP.userId
                                          ).length
                                            ? "Sì"
                                            : "No"}</strong>
                                        </ExtendedTypography>
                                        <ExtendedTypography
                                          variant="body"
                                          color="text.secondary"                                          
                                        >
                                          {t("prestazione.prossimaVerifica")}
                                          <strong>{item.rischi.filter(
                                            (x) => x.ProvidedBy === itemP.userId
                                          ).length > 0
                                            ? " " +
                                              new Date(
                                                item.rischi.filter(
                                                  (x) =>
                                                    x.ProvidedBy === itemP.userId
                                                )[0].DataControlloCostante
                                              ).toLocaleDateString()
                                            : " -"}</strong>
                                        </ExtendedTypography>
                                      </Stack>
                                      
                                      {/* Bottone per gestire il rischio del professionista */}
                                      <IconButton
                                        edge="end"
                                        aria-label={item.rischi.filter(x => x.ProvidedBy === itemP.userId).length > 0 ? "edit-rischio" : "add-rischio"}
                                        color="primary"
                                        size="small"
                                        onClick={() => handleRischioProf(item, itemP)}
                                        title={item.rischi.filter(x => x.ProvidedBy === itemP.userId).length > 0 ? "Modifica valutazione rischio" : "Aggiungi valutazione rischio"}
                                      >
                                        {item.rischi.filter(x => x.ProvidedBy === itemP.userId).length > 0 ? (
                                          <ModeEditIcon fontSize="small" />
                                        ) : (
                                          <AddIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Stack>
                                    
                                    {item.professionisti.length > 1 ? (
                                      <Divider
                                        variant="standard"
                                        sx={{ mt: 1, mb: 1 }}
                                      />
                                    ) : null}                                    
                                  </Stack>
                                ))}
                                </Fragment>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <NoData size="subtitle" />
                  )}
                </Stack>
                <Stack
                  direction="column"
                  spacing={2}
                  sx={{
                    width: "50%",
                    ...((!canIshowMezziPag || isPrestazioneReadOnly) && { width: "75%" }),
                  }}
                >
                  {showPrestazioneForm ? (
                    <Fragment>
                      {isPrestazioneReadOnly && (
                        <Stack 
                          direction="row" 
                          alignItems="center" 
                          spacing={1} 
                          sx={{ 
                            mb: 2, 
                            p: 1, 
                            backgroundColor: "action.hover", 
                            borderRadius: 1 
                          }}
                        >
                          <VisibilityIcon fontSize="small" color="action" />
                          <ExtendedTypography variant="body" color="text.secondary">
                            { "Prestazione di un altro tipo di professionista — potrai solo visualizzarla"}
                          </ExtendedTypography>
                        </Stack>
                      )}
                      <Box sx={isPrestazioneReadOnly ? { pointerEvents: "none", opacity: 0.7 } : {}}>
                      <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="left"
                        alignItems="center"
                      >
                        <ExtendedTypography variant="subtitle2">
                          {t("prestazione.form.tipo")}:{" "}
                        </ExtendedTypography>
                        <VaporTag
                          variant="ghost"
                          type="blueSapphire"
                          label={currentPrestazioneTipoKeyS}
                        />
                        <Box sx={{ flexGrow: 1 }} />
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                id="chkPrivate"
                                checked={currentPrestazioneChbVal.isPrivate}
                                onChange={handleChange}
                              />
                            }
                            label={t("prestazione.form.privata")}
                          />
                        </FormGroup>
                      </Stack>

                      {!isPrestazioneReadOnly && (
                      <Stack direction="row" sx={{ mt: "2px !important" }}>
                        <Button
                          variant="text"
                          size="small"
                          startIcon={
                            <img
                              src="/img/EcLogo.png"
                              width={20}
                              alt="EcLogo"
                            />
                          }
                          sx={{ width: "fit-content !important" }}
                          onClick={() => handleEuroConfClick(17239)}
                        >
                          {t("prestazione.adeguataVerifica")}
                        </Button>
                      </Stack>
                      )}

                      <Stack direction="row" spacing={2}>
                        <Autocomplete
                          id="ddlUtenti"
                          key={uuidv4()}
                          size="small"
                          fullWidth
                          multiple
                          options={utentiList}
                          sx={{ width: "100%" }}
                          getOptionLabel={(option) => option.completeName}
                          isOptionEqualToValue={(option, value) =>
                            option.userId === value.userId
                          }
                          defaultValue={currentPrestazione.professionisti}
                          renderOption={(props, option, { selected }) => (
                            <li {...props}>
                              <Checkbox
                                id="chbDdlUtenti"
                                icon={
                                  <CheckBoxOutlineBlankIcon fontSize="small" />
                                }
                                checkedIcon={<CheckBoxIcon fontSize="small" />}
                                style={{ marginRight: 8 }}
                                checked={selected}
                              />
                              {option.completeName}
                            </li>
                          )}
                          getOptionDisabled={(option) =>
                            currentPrestazione.rischi.length > 0 &&
                            currentPrestazione.rischi.filter(
                              (x) => x.ProvidedBy === option.userId
                            ).length > 0
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t("prestazione.form.erogataDa")}
                              required
                              onKeyDown={(event) => {
                                event.stopPropagation();
                              }}
                            />
                          )}
                          renderTags={(params) =>
                            params.map((item) => (
                              <VaporTag
                                key={uuidv4()}
                                variant="standard"
                                type="blueSapphire"
                                label={item.completeName}
                                sx={{ mr: 1 }}
                              />
                            ))
                          }
                          onChange={(event, newValue, reason) => {
                            handleChange(event, newValue);
                            if (event.code !== "Backspace") {
                            }
                          }}
                        />

                        <LocalizationProvider
                          dateAdapter={AdapterDateFns}
                          adapterLocale={it}
                        >
                          <DatePicker
                            id="dpStartDate"
                            name="dpStartDate"
                            label={t("prestazione.form.dataInizio")}
                            closeOnSelect
                            format="dd/MM/yyyy"
                            mask="__/__/____"
                            maxDate={
                              currentPrestazione.rischi.length > 0 &&
                              isDateValid(currentPrestazione.rischi[0].Date)
                                ? new Date(currentPrestazione.rischi[0].Date)
                                : null
                            }
                            minDate={
                              anagraficaFascicolo && isDateValid(anagraficaFascicolo.identifiedOn)
                                ? new Date(anagraficaFascicolo.identifiedOn)
                                : new Date()
                            }
                            value={
                              isDateValid(currentPrestazione.dataStart)
                                ? new Date(currentPrestazione.dataStart)
                                : null
                            }
                            onChange={handleStartDate}
                            slotProps={{
                              textField: {
                                required: true,
                                placeholder: "dd/MM/yyyy",
                                size: "small",
                                fullWidth: true,
                              },
                            }}
                          />
                        </LocalizationProvider>

                        <LocalizationProvider
                          dateAdapter={AdapterDateFns}
                          adapterLocale={it}
                        >
                          <DatePicker
                            id="dpEndDate"
                            name="dpEndDate"
                            label={t("prestazione.form.dataCessazione")}
                            closeOnSelect
                            format="dd/MM/yyyy"
                            mask="__/__/____"
                            minDate={
                              isDateValid(currentPrestazione.dataStart)
                                ? new Date(currentPrestazione.dataStart)
                                : null
                            }
                            value={
                              isDateValid(currentPrestazione.dataEnd)
                                ? new Date(currentPrestazione.dataEnd)
                                : null
                            }
                            onChange={handleEndDate}
                            slotProps={{
                              textField: {
                                placeholder: "dd/MM/yyyy",
                                size: "small",
                                fullWidth: true,
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Stack>
                      <Stack direction="row" spacing={2}>                        
                        <Autocomplete
                          id="ddlPrestazione"
                          key={uuidv4()}
                          size="small"
                          fullWidth
                          options={baseTipoPrestazioneList}
                          sx={{ width: "100%" }}
                          isOptionEqualToValue={(option, value) =>
                            currentPrestazione.prestazioneId === undefined
                              ? option.id === value.id
                              : option.id === currentPrestazione.prestazioneId
                          }
                          getOptionLabel={(option) =>
                            option.nome                            
                          }
                          defaultValue={
                            currentPrestazione.prestazioneId !== undefined
                              ? baseTipoPrestazioneList.filter(
                                  (item) =>
                                    item.id === currentPrestazione.prestazioneId
                                )[0]
                              : null
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t("prestazione.form.prestazione")}
                              required
                            />
                          )}
                          onChange={handleChange}
                        />

                        <Autocomplete
                          id="ddlScopo"
                          key={uuidv4()}
                          size="small"
                          fullWidth
                          options={baseScopoPrestazioneList}
                          sx={{ width: "100%" }}
                          isOptionEqualToValue={(option, value) =>
                            currentPrestazione.scopoId === undefined
                              ? option.id === value.id
                              : option.id === currentPrestazione.scopoId
                          }
                          getOptionLabel={(option) => option.value}
                          defaultValue={
                            currentPrestazione.scopoId !== undefined
                              ? baseScopoPrestazioneList.filter(
                                  (item) =>
                                    item.id === currentPrestazione.scopoId
                                )[0]
                              : null
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t("prestazione.form.scopoRapporto")}
                              required
                            />
                          )}
                          onChange={handleChange}
                        />

                        <Autocomplete
                          id="ddlNatura"
                          key={uuidv4()}
                          size="small"
                          fullWidth
                          options={baseNaturaPrestazioneList}
                          sx={{ width: "100%" }}
                          isOptionEqualToValue={(option, value) =>
                            currentPrestazione.naturaId === undefined
                              ? option.id === value.id
                              : option.id === currentPrestazione.naturaId
                          }
                          getOptionLabel={(option) => option.value}
                          defaultValue={
                            currentPrestazione.naturaId !== undefined
                              ? baseNaturaPrestazioneList.filter(
                                  (item) =>
                                    item.id === currentPrestazione.naturaId
                                )[0]
                              : null
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t("prestazione.form.naturaRapporto")}
                              required
                            />
                          )}
                          onChange={handleChange}
                        />
                      </Stack>
                      <Stack direction="row" spacing={2}>
                        <Autocomplete
                          id="ddlPaese"
                          key={uuidv4()}
                          size="small"
                          fullWidth
                          options={basePaeseList}
                          sx={{ width: "100%" }}
                          isOptionEqualToValue={(option, value) =>
                            currentPrestazione.paeseId === undefined
                              ? option.id === value.id
                              : option.id === currentPrestazione.paeseId
                          }
                          getOptionLabel={(option) => option.nome}
                          defaultValue={
                            currentPrestazione.paeseId !== undefined
                              ? basePaeseList.filter(
                                  (item) =>
                                    item.id === currentPrestazione.paeseId
                                )[0]
                              : null
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t("prestazione.form.nazione")}
                              required
                            />
                          )}
                          onChange={handleChange}
                        />

                        {isPaeseIta ? (
                          <Autocomplete
                            id="ddlProvincia"
                            key={uuidv4()}
                            size="small"
                            fullWidth
                            options={baseProvinciaList}
                            sx={{ width: "100%" }}
                            isOptionEqualToValue={(option, value) =>
                              currentPrestazione.provinciaId === undefined
                                ? option.id === value.id
                                : option.id === currentPrestazione.provinciaId
                            }
                            getOptionLabel={(option) => option.nome}
                            defaultValue={
                              currentPrestazione.provinciaId !== undefined
                                ? baseProvinciaList.filter(
                                    (item) =>
                                      item.id === currentPrestazione.provinciaId
                                  )[0]
                                : null
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t("prestazione.form.provincia")}
                              />
                            )}
                            onChange={handleChange}
                          />
                        ) : null}

                        {!isPaeseIta ? (
                          <TextField
                            id="txtProvincia"
                            key={uuidv4()}
                            label={t("prestazione.form.provincia")}
                            size="small"
                            defaultValue={currentPrestazione.provincia}
                            fullWidth
                            onChange={handleChange}
                            inputProps={{ autocomplete: "nope",maxLength:500 }}
                          />
                        ) : null}

                        {isPaeseIta ? (
                          <Autocomplete
                            id="ddlComune"
                            key={uuidv4()}
                            size="small"
                            fullWidth
                            options={baseComuneList}
                            sx={{ width: "100%" }}
                            isOptionEqualToValue={(option, value) =>
                              currentPrestazione.cittaId === undefined
                                ? option.id === value.id
                                : option.id === currentPrestazione.cittaId
                            }
                            getOptionLabel={(option) => option.nome}
                            defaultValue={
                              currentPrestazione.cittaId !== undefined
                                ? baseComuneList.filter(
                                    (item) =>
                                      item.id === currentPrestazione.cittaId
                                  )[0]
                                : null
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t("prestazione.form.comune")}
                              />
                            )}
                            onChange={handleChange}
                          />
                        ) : null}

                        {!isPaeseIta ? (
                          <TextField
                            id="txtComune"
                            key={uuidv4()}
                            label={t("prestazione.form.comune")}
                            size="small"
                            defaultValue={currentPrestazione.citta}
                            fullWidth
                            onChange={handleChange}
                            inputProps={{ autocomplete: "nope",maxLength:500 }}
                          />
                        ) : null}
                      </Stack>
                      <Stack direction="column" spacing={2}>
                        {tenantTypeId === 3 &&
                        <>
                          <TextField
                            id="txtSettoreAttivita"
                            key={uuidv4()}
                            label="Settore di attività in cui opera generalmente il cliente"
                            size="small"
                            defaultValue={currentPrestazione.settoreAttivita}                          
                            fullWidth
                            multiline                                                      
                            onChange={handleChange}                          
                            inputProps={{ autocomplete: "nope", maxLength : 1024 }}                          
                          />
                          <TextField
                            id="txtSettorePrestazione"
                            key={uuidv4()}
                            label="Settore di attività connesso all’oggetto della prestazione richiesta"
                            size="small"
                            defaultValue={currentPrestazione.settorePrestazione}                          
                            fullWidth
                            multiline                                                      
                            onChange={handleChange}                          
                            inputProps={{ autocomplete: "nope", maxLength : 1024 }}                          
                          />
                          <TextField
                            id="txtRagione"
                            key={uuidv4()}
                            label="Ragione per la quale il cliente intende effettuare l’operazione"
                            size="small"
                            defaultValue={currentPrestazione.ragione}                          
                            fullWidth
                            multiline                                                    
                            onChange={handleChange}                          
                            inputProps={{ autocomplete: "nope", maxLength : 1024 }}                          
                          />
                        </>                    
                        }

                        <TextField
                          id="txtDescrizione"
                          key={uuidv4()}
                          label={t("prestazione.form.descrizione")}
                          size="small"
                          defaultValue={currentPrestazione.descrizione}                          
                          fullWidth
                          multiline
                          required
                          rows={3}
                          onChange={handleChange}                          
                          inputProps={{ autocomplete: "nope", maxLength : 1000 }}                          
                        />
                      </Stack>

                      <Stack direction="column" spacing={2}>
                        <ExtendedAccordion>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <ExtendedTypography
                              variant="titleSmall"
                              color="primary.main"
                            >
                              {t("prestazione.astensione")}
                            </ExtendedTypography>
                          </AccordionSummary>
                          <AccordionDetails
                            sx={{
                              pl: "15px !important",
                              pr: "15px !important",
                            }}
                          >
                            <Grid container>
                              <Grid item xs={12}>
                                {!isPrestazioneReadOnly && (
                                <Button
                                  variant="text"
                                  size="small"
                                  startIcon={
                                    <img
                                      src="/img/EcLogo.png"
                                      width={20}
                                      alt="EcoLogo"
                                    />
                                  }
                                  sx={{ width: "fit-content !important" }}
                                  onClick={() => handleEuroConfClick(17235)}
                                >
                                  {t("prestazione.oblighi")}
                                </Button>
                                )}
                                <FormGroup>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        id="chkAstIsEsonero"
                                        checked={
                                          currentPrestazioneChbVal.isEsoneroObbAst
                                        }
                                        onChange={handleChange}
                                      />
                                    }
                                    label="Astensione dall’esecuzione della prestazione"
                                  />
                                </FormGroup>
                              </Grid>
                              {currentPrestazioneChbVal.isEsoneroObbAst ? (
                                <Grid item xs={12}>
                                  <TextField
                                    id="txtAstMotivo"
                                    key={uuidv4()}
                                    label="Eventuale motivazione di astensione"
                                    size="small"
                                    defaultValue={
                                      currentPrestazione.astensioneMotivo
                                    }
                                    fullWidth
                                    multiline
                                    rows={2}
                                    onChange={handleChange}
                                    inputProps={{ autocomplete: "nope",maxLength:1000 }}
                                  />
                                </Grid>
                              ) : null}
                            </Grid>
                          </AccordionDetails>
                        </ExtendedAccordion>
                      </Stack>
                      </Box>
                    </Fragment>
                  ) : null}
                </Stack>

                {canIshowMezziPag && !isPrestazioneReadOnly ? (
                  <Stack direction="column" spacing={2} sx={{ width: "25%" }}>
                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <ExtendedTypography
                        variant="titleSmall"
                        color="primary.main"
                      >
                        {t("prestazione.mezzoPagamento.titoloList")}
                      </ExtendedTypography>
                      <Box flexGrow="1" />
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Switch
                              id="chbShowMezziPag"
                              checked={showMezziPag}
                              onChange={handleChangePagamento}
                            />
                          }
                          label=""
                          disabled={currentPrestazione.id === 0}
                        />
                      </FormGroup>
                    </Stack>
                    <Divider variant="standard" />

                    {showMezziPag ? (
                      <Grid container>
                        <Grid item xs={12}>
                          <Stack direction="column" spacing={2}>
                            <LocalizationProvider
                              dateAdapter={AdapterDateFns}
                              adapterLocale={it}
                            >
                              <DatePicker
                                id="dpPagamentoDate"
                                name="dpPagamentoDate"
                                label={`${t("autovalutazione.form.data")} di pagamento`}
                                closeOnSelect
                                format="dd/MM/yyyy"
                                mask="__/__/____"
                                value={
                                  isDateValid(currentPrestazionePagamento.data)
                                    ? new Date(currentPrestazionePagamento.data)
                                    : null
                                }
                                onChange={handlePagamentoDate}
                                minDate={
                                  isDateValid(currentPrestazione.dataStart)
                                    ? new Date(currentPrestazione.dataStart)
                                    : null
                                }
                                maxDate={new Date()}
                                slotProps={{
                                  textField: {
                                    required: true,
                                    placeholder: "dd/MM/yyyy",
                                    size: "small",
                                    fullWidth: true,
                                  },
                                }}
                              />
                            </LocalizationProvider>

                            <Stack
                              direction="row"
                              spacing={2}
                              visibility={
                                !userPermissions.canCreate ? "hidden" : "block"
                              }
                            >
                              <Grid item xs={5}>
                                <LocalizationProvider
                                  dateAdapter={AdapterDateFns}
                                  adapterLocale={it}
                                >
                                  <DatePicker
                                    id="dpValutaCambioData"
                                    name="dpValutaCambioData"
                                    label={t(
                                      "prestazione.mezzoPagamento.form.dataCambio"
                                    )}
                                    closeOnSelect
                                    format="dd/MM/yyyy"
                                    mask="__/__/____"
                                    minDate={
                                      isDateValid(currentPrestazionePagamento.data)
                                        ? new Date(currentPrestazionePagamento.data)
                                        : null
                                    }
                                    maxDate={new Date()}
                                    value={
                                      isDateValid(
                                        currentPrestazionePagamento.valutaCambioData
                                      )
                                        ? new Date(
                                            currentPrestazionePagamento.valutaCambioData
                                          )
                                        : null
                                    }
                                    required
                                    disabled={currentPrestazione.id === 0}
                                    onChange={handleValutaCambioDate}
                                    slotProps={{
                                      textField: {
                                        required: true,
                                        placeholder: "dd/MM/yyyy",
                                        size: "small",
                                        fullWidth: true,
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              </Grid>
                              <Grid item xs={4}>
                                <Autocomplete
                                  id="ddlValuta"
                                  key={uuidv4()}
                                  size="small"
                                  fullWidth
                                  options={baseValutaList}
                                  sx={{ width: "100%" }}
                                  isOptionEqualToValue={(option, value) =>
                                    currentPrestazionePagamento.valutaId ===
                                    undefined
                                      ? option.isoCode === value.isoCode
                                      : option.isoCode ===
                                        currentPrestazionePagamento.isoCode
                                  }
                                  getOptionLabel={(option) => option.isoCode}
                                  value={
                                    baseValutaList.find(
                                      (item) => item.isoCode === currentPrestazionePagamento.isoCode
                                    ) || baseValutaList.find((item) => item.isoCode === "EUR")
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={t(
                                        "prestazione.mezzoPagamento.form.valuta"
                                      )}
                                      required
                                    />
                                  )}
                                  disabled={currentPrestazione.id === 0}
                                  onChange={handleChangePagamento}
                                />
                              </Grid>
                              <Grid item xs={3}>
                                <TextField
                                  id="txtImporto"
                                  key={uuidv4()}
                                  ref={txtImportoRef}
                                  label={t(
                                    "prestazione.mezzoPagamento.form.importo"
                                  )}
                                  defaultValue={currentPrestazionePagamento.valutaImporto.toLocaleString(
                                    "it-IT",
                                    {
                                      maximumFractionDigits: 2,
                                      minimumFractionDigits: 2,
                                      maximumDigits: 8,
                                    }
                                  )}
                                  size="small"
                                  fullWidth
                                  required
                                  disabled={currentPrestazione.id === 0}
                                  onBlur={handleBlurImportoPagamento}                                  
                                  inputProps={{ autocomplete: "nope" }}
                                />                        
                              </Grid>
                            </Stack>

                            {isValutaEstera ? (
                              <Stack
                                direction="row"
                                spacing={2}
                              >
                                <Grid item xs={6}>
                                  <TextField
                                    id="txtValutaCambio"
                                    label={t(
                                      "prestazione.mezzoPagamento.form.cambio"
                                    )}
                                    value={valutaCambioInput}
                                    size="small"
                                    fullWidth
                                    required
                                    onChange={handleChangePagamento}
                                    onBlur={handleBlurValutaCambio}
                                    disabled={currentPrestazionePagamento.valutaCambio !== 0}
                                    inputProps={{ autocomplete: "nope" }}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <TextField
                                    id="txtImportoEur"
                                    key={uuidv4()}
                                    label={t(
                                      "prestazione.mezzoPagamento.form.importoEuro"
                                    )}
                                    defaultValue={pagamentoImportoEuro.toLocaleString(
                                      "it-IT",
                                      {
                                        maximumFractionDigits: 2,
                                        minimumFractionDigits: 2,
                                      }
                                    )}
                                    size="small"
                                    fullWidth
                                    required={isValutaEstera}
                                    disabled={true}
                                    inputProps={{ autocomplete: "nope" }}
                                  />
                                </Grid>
                              </Stack>
                            ) : null}

                            <Stack direction="row" spacing={2}>
                              <Grid item xs={6}>
                                <Autocomplete
                                  id="ddlMezzoPagamento"
                                  key={uuidv4()}
                                  size="small"
                                  fullWidth
                                  options={baseMezzoPagamentoList}
                                  sx={{ width: "100%" }}
                                  isOptionEqualToValue={(option, value) =>
                                    currentPrestazionePagamento.modalitaPagamentoId ===
                                    undefined
                                      ? option.id === value.id
                                      : option.id ===
                                        currentPrestazionePagamento.modalitaPagamentoId
                                  }
                                  getOptionLabel={(option) => option.value}
                                  defaultValue={
                                    currentPrestazionePagamento.modalitaPagamentoId !==
                                    undefined
                                      ? baseMezzoPagamentoList.filter(
                                          (item) =>
                                            item.id ===
                                            currentPrestazionePagamento.modalitaPagamentoId
                                        )[0]
                                      : null
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={t(
                                        "prestazione.mezzoPagamento.form.mezzoPagamento"
                                      )}
                                      required
                                    />
                                  )}
                                  disabled={currentPrestazione.id === 0}
                                  onChange={handleChangePagamento}
                                />
                              </Grid>
                              <Grid item xs={6}>
                                <Autocomplete
                                  id="ddlOrigineFondi"
                                  key={uuidv4()}
                                  size="small"
                                  fullWidth
                                  options={baseOrigineFondiList}
                                  sx={{ width: "100%" }}
                                  isOptionEqualToValue={(option, value) =>
                                    currentPrestazionePagamento.origineId ===
                                    undefined
                                      ? option.id === value.id
                                      : option.id ===
                                        currentPrestazionePagamento.origineId
                                  }
                                  getOptionLabel={(option) => option.value}
                                  defaultValue={
                                    currentPrestazionePagamento.origineId !==
                                    undefined
                                      ? baseOrigineFondiList.filter(
                                          (item) =>
                                            item.id ===
                                            currentPrestazionePagamento.origineId
                                        )[0]
                                      : null
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={t(
                                        "prestazione.mezzoPagamento.form.origineFondi"
                                      )}
                                      required
                                    />
                                  )}
                                  disabled={currentPrestazione.id === 0}
                                  onChange={handleChangePagamento}
                                />
                              </Grid>
                            </Stack>
                            {userPermissions.canCreate ? (
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="right"
                                alignItems="right"
                              >
                                <Button
                                  variant="outlined"
                                  color="secondary"
                                  size="small"
                                  disabled={currentPrestazione.id === 0}
                                  onClick={handleCancelPagamentoClick}
                                >
                                  {t("actions.annulla")}
                                </Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  disabled={currentPrestazione.id === 0}
                                  onClick={handleSavePagamentoClick}
                                >
                                  {t("actions.salva")}
                                </Button>
                              </Stack>
                            ) : null}

                            <ExtendedTypography
                              variant="titleSmall"
                              color="primary.main"
                            >
                              {t("fascicolo.fascicoloAdd.elenco")} dei pagamenti (€ {currentPrestazionePagamentiList.reduce((sum, item) => sum + item.importo, 0).toLocaleString("it-IT", { maximumFractionDigits: 2, minimumFractionDigits: 2 })})
                            </ExtendedTypography>
                            <Divider variant="standard" sx={{ mt: 1, mb: 1 }} />

                            <Stack direction="column" spacing={2}>
                              {currentPrestazionePagamentiList.length > 0 ? (
                                <TableContainer component={Paper}>
                                  <Table size="small">
                                    <TableBody>
                                      {currentPrestazionePagamentiList.map(
                                        (item) => (
                                          <TableRow
                                            key={uuidv4()}
                                            sx={{
                                              "&:last-child td, &:last-child th":
                                                { border: 0 },
                                            }}
                                            hover
                                            selected={
                                              currentPrestazionePagamentiList.id === item.id
                                            }
                                          >
                                            <TableCell
                                              key={uuidv4()}
                                              component="th"
                                              scope="row"
                                            >
                                              <Stack
                                                direction="row"
                                                justifyContent="center"
                                                alignItems="center"
                                              >
                                                <Stack direction="column">
                                                  <Stack
                                                    direction="row"
                                                    spacing={1}
                                                  >
                                                    <ExtendedTypography
                                                      variant="titleSmall"
                                                      color="primary.main"
                                                    >
                                                      {"€ " +
                                                        item.importo.toLocaleString(
                                                          "it-IT",
                                                          {
                                                            maximumFractionDigits: 2,
                                                            minimumFractionDigits: 2,
                                                          }
                                                        )}
                                                    </ExtendedTypography>
                                                  </Stack>

                                                  <ExtendedTypography
                                                    variant="body"
                                                    color="text.secondary"
                                                  >
                                                    {t("prestazione.importo")}{" "}
                                                    {item.isoCode +
                                                      " " +
                                                      item.valutaImporto.toLocaleString(
                                                        "it-IT",
                                                        {
                                                          maximumFractionDigits: 2,
                                                          minimumFractionDigits: 2,
                                                        }
                                                      )}{" "}
                                                    {t("prestazione.conCambio")}{" "}
                                                    {item.isoCode === "EUR"
                                                      ? (1).toLocaleString(
                                                          "it-IT",
                                                          {
                                                            maximumFractionDigits: 2,
                                                            minimumFractionDigits: 2,
                                                          }
                                                        )
                                                      : (
                                                          item.valutaCambio
                                                        ).toLocaleString(
                                                          "it-IT",
                                                          {
                                                            maximumFractionDigits: 2,
                                                            minimumFractionDigits: 2,
                                                          }
                                                        )}{" "}
                                                    {t(
                                                      "prestazione.allaDataDel"
                                                    )}{" "}
                                                    {new Date(
                                                      item.valutaCambioData
                                                    ).toLocaleDateString()}
                                                  </ExtendedTypography>
                                                  <ExtendedTypography
                                                    variant="body"
                                                    color="text.secondary"
                                                  >
                                                    {t(
                                                      "prestazione.pagadtoData"
                                                    )}{" "}
                                                    {new Date(
                                                      item.data
                                                    ).toLocaleDateString()}
                                                  </ExtendedTypography>
                                                  <ExtendedTypography
                                                    variant="body"
                                                    color="text.secondary"
                                                  >
                                                    {t(
                                                      "prestazione.pagataMezzo"
                                                    )}{" "}
                                                    {item.modalitaPagamentoS}
                                                  </ExtendedTypography>
                                                  <ExtendedTypography
                                                    variant="body"
                                                    color="text.secondary"
                                                  >
                                                    {t()} {item.origineS}
                                                  </ExtendedTypography>
                                                </Stack>
                                                <Box sx={{ flexGrow: 1 }} />
                                                <Stack direction="row">
                                                  <IconButton
                                                    color="primary"
                                                    disabled={
                                                      !userPermissions.canUpdate
                                                    }
                                                    onClick={() =>
                                                      handleSelectedPagamentoClick(
                                                        item
                                                      )
                                                    }
                                                  >
                                                    <ModeEditIcon fontSize="small" />
                                                  </IconButton>
                                                  <IconButton
                                                    color="error"
                                                    disabled={
                                                      !userPermissions.canDelete
                                                    }
                                                    onClick={() =>
                                                      handleDeletePagamentoClick(
                                                        item
                                                      )
                                                    }
                                                  >
                                                    <DeleteIcon fontSize="small" />
                                                  </IconButton>
                                                </Stack>
                                              </Stack>
                                            </TableCell>
                                          </TableRow>
                                        )
                                      )}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              ) : (
                                <NoData size="subtitle" />
                              )}
                            </Stack>
                          </Stack>
                        </Grid>
                      </Grid>
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>
            </Grid>
          </Grid>
        ) : null}
      </VaporPage.Section>
      {isDataLoaded && userPermissions.canCreate && showPrestazioneForm && !isPrestazioneReadOnly ? (
        <VaporToolbar
          variant="regular"
          size="medium"
          withoutAppBar={false}
          contentRight={
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                color="secondary"
                size="medium"
                endIcon={<CancelIcon />}
                onClick={handleCancelClick}
              >
                {t("actions.annulla")}
              </Button>
              <Button
                variant="contained"
                size="medium"
                endIcon={<SaveIcon />}
                onClick={handleSaveClick}
              >
                {currentPrestazione.id === 0
                  ? t("actions.salva")
                  : t("actions.aggiorna")}
              </Button>
            </Stack>
          }
        ></VaporToolbar>
      ) : null}
      {isDataLoaded && showPrestazioneForm && isPrestazioneReadOnly ? (
        <VaporToolbar
          variant="regular"
          size="medium"
          withoutAppBar={false}
          contentRight={
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                color="secondary"
                size="medium"
                endIcon={<CancelIcon />}
                onClick={handleCancelClick}
              >
                {t("actions.chiudi") || "Chiudi"}
              </Button>
            </Stack>
          }
        ></VaporToolbar>
      ) : null}
    </Fragment>
  );
  //#endregion
};
